/**
 * The contacts table — the load-bearing dynamic-column renderer (subtask 05).
 *
 * Renders **BASE columns ALWAYS** (select · Name · Export · Thrive ID ·
 * Contact info) **plus
 * one column per `columns[]` entry** the API returns (the descriptor-driven
 * dynamic annex columns) and, on a Company Map list, three Matched-* columns.
 * The base block is PINNED LEFT and everything type-specific follows it
 * (Trello #711 cl.4 items 7 + 14, as clarified 2026-08-20) — so a column sits
 * in the same place whichever of the three list types you are looking at. The
 * column set still adapts per import with zero rework, which is the point of
 * this subtask.
 *
 * The contact's **Name links to their LinkedIn profile** (`linkedin_url`) — the
 * standalone "LinkedIn URL" column was removed since the URL is identity, not a
 * value worth its own wide column. **Every** column is drag-resizable via a
 * right-edge handle (checklist 7); before that, only the dynamic annex columns
 * were, which meant a Raw Import or Company Map list had none. See
 * `useColumnWidths` for what is persisted and when.
 *
 * Column layout:
 *   - **Name**: name · company/title. A revision put the emails here as a
 *     third line and deleted their column; that was reversed (cl.4 item 7
 *     lists Contact info among the mandatory base columns), so the name cell
 *     holds identity only.
 *   - **Added to search**: what happened to this contact IN THIS SEARCH, and
 *     only that (see `SearchStatusCellContent`). Added / Failed / Ignored / To
 *     review, and "Not added yet" wherever nothing has been added. No link —
 *     the deep link to Thrive is its own column.
 *   - **Thrive ID**: whether we know this person in Thrive.
 *     `contact_id_thrive` linked to the real Thrive contact page, shown
 *     whatever the export outcome and INCLUDING on a skipped row (cl.4 items
 *     11 + 13, reversing the 2026-07-15 review — see the cell); "Not found ·
 *     <date>" once a compare classified THIS ROW and Thrive had no such
 *     contact; "Not compared yet" when none ever has.
 *
 *   The two columns were restructured on 2026-09-08 (operator, hand-testing)
 *   because neither matched its own header: "Added to search" carried the whole
 *   `review_status`, including three Thrive-side states that say nothing about
 *   whether the contact was added to this search, while "Thrive ID" held an id
 *   or a dash and stayed silent on whether we had ever looked. One
 *   `review_status` still feeds both — the split is presentation, not
 *   derivation.
 *   - **Contact info**: both addresses, their provenance badges and any
 *     enrichment-failure warning (cl.4 item 8, renamed from "Enrich Data").
 *     It CLOSES the base block rather than trailing the dynamic columns as it
 *     used to, so its position no longer varies by list type. Pinned at
 *     `CONTACT_INFO_WIDTH` from mount — see `useColumnWidths` for why that
 *     default is the bug fix and not decoration.
 *   - **Connection**: REMOVED (cl.4 item 7). `connection_degree` stays in the
 *     API — the BCT CSV export writes it independently.
 *
 * **Exactly one thing truncates: the email address** (Lucas, 2026-08-21,
 * narrowing cl.4 item 6). It clips to one line with an ellipsis and carries the
 * full value in a `title`. Everything else — the name, the company/title line,
 * the AI Review rationale, the Company Map columns — renders in full. The clip
 * is `line-clamp-1` and NOT `truncate`, because `truncate` sets `nowrap` and a
 * nowrap address becomes the column's min-content: measured, the Contact info
 * column then stalls somewhere between 140px and 413px depending on the longest
 * address on the page, instead of reaching its 128px clamp — defeating
 * checklist 7's drag outright. See `EnrichData` for the three measurements.
 *
 * **Nothing else here truncates** (cl.4 item 6). Cells that hold an unbreakable
 * token carry `[overflow-wrap:anywhere]` instead. That rule matters at the
 * CLAMP — measured, a Contact info column dragged to its floor reaches 128px
 * with the rule and stalls at 140px without it — but it does NOT stop a column
 * being squeezed in the first place: `anywhere` is counted in min-content
 * sizing exactly as `break-all` is (CSS Text 3; only `break-word` is not), and
 * the two measure identical on an unpinned column. What stops the squeeze is a
 * pinned width. An earlier version of this comment credited the wrap rule with
 * the whole fix; `/pr-review` refuted it.
 *
 * Filtering is SERVER-SIDE (2026-08-14 PRD): every filter control here —
 * the `EnrichmentFilterBar` / `ReviewStatusFilterBar` chips, the BCT Filters
 * (company / position / years), and the name search — is a CONTROLLED input
 * whose state lives in `ImportDetailPage`, which forwards it as query params
 * to `GET /imports/{id}/contacts`. The `contacts` prop arrives already
 * filtered + paginated; this component renders it as-is. Chip counts come
 * from the response's `counts`, computed over the whole import under the OTHER
 * active filters (a chip never narrows its own bar).
 * Selection checkboxes feed the parent's Export-Selected state (subtask 07).
 *
 * Tokens only — the prototype's hard-coded hex are translated to design-system
 * tokens; no arbitrary `text-[Npx]`. The only inline `style={{}}` is a column
 * `width`, emitted by `widthStyle()` for any column that has been resized and
 * always for the two kinds that ship pinned — the annex columns and Contact
 * info (the sanctioned dynamic-value exception, same
 * class as DockablePanel's runtime position / NewImportPage's progress width);
 * every static style stays a token utility.
 */
import type { AnnexColumn, ImportContactRow, ImportContactsCounts } from "@nu-assist/contracts";
import { Avatar, Tooltip } from "@nu-assist/ui";
import {
  AlertTriangle,
  CircleDashed,
  CircleX,
  Copy,
  EyeOff,
  Linkedin,
  type LucideIcon,
  SearchX,
  UserPlus,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { ThriveIdCell } from "../../../components/ThriveIdCell";
import { StageChips } from "../../../components/stages/StageChips";
import { StagePill } from "../../../components/stages/StagePill";
import { REJECTED_STAGE } from "../../../components/stages/ladder";
import { isExportable } from "../../../features/imports-export/isExportable";
import { UNDO_DIFFERENT_PERSON_LABEL } from "../../../features/imports/conflictActionsCopy";
import {
  ALREADY_DUPLICATED_HINT,
  DATE_NOT_RECORDED,
  THRIVE_NOT_COMPARED,
  THRIVE_NOT_FOUND,
  WILL_DUPLICATE_HINT,
  WILL_DUPLICATE_MARKER,
  searchStatusLabel,
} from "../../../features/imports/reviewStatusCopy";
import { linkedinHandle } from "../../../features/person-panel/personHandle";
import { formatDate, formatDaysInStage } from "../../../lib/format/date";
import { ContactFilterBar } from "./ContactFilterBar";
import { EnrichData } from "./EnrichData";
import { type EnrichmentFilter, EnrichmentFilterBar } from "./EnrichmentFilterBar";
import { InfoHint } from "./InfoHint";
import { NameSearchBar } from "./NameSearchBar";
import { NotesSearchBar } from "./NotesSearchBar";
import {
  type AddToSearchFilter,
  type ReviewStatusFilter,
  ReviewStatusFilterBar,
  type ThriveIdFilter,
  isInThrive,
} from "./ReviewStatusFilterBar";
import { ScoreBadge } from "./ScoreBadge";
import { filterInputClass } from "./filterInputClass";
import {
  MAX_FONT_REMEASURES,
  type NaturalWidths,
  fitWidth,
  measureColumnWidths,
  pendingFontLoad,
} from "./measureColumnWidths";
import { findNotesRanges } from "./notesSearch";
import {
  CONTACT_INFO_WIDTH,
  DEFAULT_ANNEX_WIDTH,
  MIN_WIDTH,
  type ResizeIntent,
  STEP,
  clampWidth,
  movesWrongWay,
  useColumnWidths,
} from "./useColumnWidths";
import type { ContactsSelectionMode } from "./useContactsSelection";

function fullName(row: ImportContactRow): string {
  const parts = [row.first_name, row.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "—";
}

/**
 * The line under the name: `<company>, <role>` (company first, per the
 * requirement), joined only over the halves that exist — one alone renders
 * without a dangling comma, and a contact whose job history yielded neither
 * gets `null` so no empty line is drawn.
 */
function currentPositionLine(row: ImportContactRow): string | null {
  const parts = [row.current_company, row.current_position].filter((part): part is string =>
    Boolean(part?.trim())
  );
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * The second line under the contact's name: `<company>, <role>`, halves
 * rendered separately so each can highlight its own filter's match (the same
 * affordance the name gets from `q`).
 *
 * It used to carry `max-w` + `truncate` (PR #691, because `truncate`'s nowrap
 * makes the full string the cell's min-content and WIDENS the sticky Name
 * column instead of ellipsizing). Both are GONE — cl.4 item 6 says the text is
 * always fully readable. The line now wraps, and `title` is a hover
 * convenience rather than the reveal affordance for clipped text. See the
 * comment on the element itself.
 */
function CurrentPositionLine({
  row,
  positionQuery,
  companyQuery,
}: {
  row: ImportContactRow;
  positionQuery: string;
  companyQuery: string;
}) {
  const line = currentPositionLine(row);
  if (!line) {
    return null;
  }
  const company = row.current_company?.trim();
  const position = row.current_position?.trim();
  return (
    <span
      data-testid="current-position-line"
      title={line}
      // No cap and no `truncate` (cl.4 item 6). The cap existed because a
      // nowrap string becomes the column min-content under `table-layout:
      // auto` and re-widens the sticky Name column. Dropping it is affordable
      // because Name now carries a `min-w` FLOOR instead: the column can no
      // longer be squeezed to the point where a job title wraps into a row
      // tens of lines tall (measured before the floor: Name at 99px, row at
      // 1032px).
      className="text-caption text-text-secondary"
    >
      {company ? highlightMatch(company, companyQuery) : null}
      {company && position ? ", " : null}
      {position ? highlightMatch(position, positionQuery) : null}
    </span>
  );
}

/**
 * Split `text` around case-insensitive occurrences of `query` and wrap each
 * match in a highlighted `<mark>`. Returns `text` unchanged for an empty query
 * or when `query` doesn't occur. Highlights from a single character. (Mirrors
 * `highlightMatch` in research/CompanyTypeahead.tsx — the actual filtering is
 * done by the parent; this only visually marks the matched substring.)
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (trimmed.length === 0) return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === trimmed.toLowerCase() ? (
      <mark key={`${i}-${part}`} className="rounded-sm bg-mark text-mark-ink">
        {part}
      </mark>
    ) : (
      <span key={`${i}-${part}`}>{part}</span>
    )
  );
}

const NO_NOTES_TERMS: readonly string[] = [];

/**
 * Mark where the AI-notes search terms occur in a rationale (Trello #779).
 * Word-start, case-insensitive — the same reading as the server's
 * filter (`findNotesRanges`), so a highlighted word is a reason the row is on
 * screen. Same `<mark>` styling as {@link highlightMatch}.
 */
function highlightNotes(text: string, terms: readonly string[]): React.ReactNode {
  const ranges = findNotesRanges(text, terms);
  if (ranges.length === 0) return text;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start > cursor) parts.push(<span key={`t-${cursor}`}>{text.slice(cursor, start)}</span>);
    parts.push(
      <mark
        key={`m-${start}`}
        data-testid="notes-highlight"
        className="rounded-sm bg-mark text-mark-ink"
      >
        {text.slice(start, end)}
      </mark>
    );
    cursor = end;
  }
  if (cursor < text.length) parts.push(<span key={`t-${cursor}`}>{text.slice(cursor)}</span>);
  return parts;
}

/**
 * Minimum pointer travel before a gesture counts as a drag, in px.
 *
 * `moved` used to be exact equality on `clientX`, which gave the no-move-click
 * guard ZERO tolerance: one pixel of jitter on a 6px grab strip read as a drag,
 * so the guard protected only a perfectly still click (`/pr-review` iter 8,
 * Agent C C10).
 */
const DRAG_TOLERANCE_PX = 3;

/**
 * Minimum pointer travel before an AMPLIFIED gesture may paint or commit, in px.
 *
 * A gesture is amplified when the column would move further than the pointer
 * did — the clamp doing the rest of the work. That is only reachable from a
 * start width BELOW `MIN_WIDTH`, and it is exactly where the direction rule
 * stops helping — and the direction is the OPPOSITE of the one this paragraph
 * named while it described the ceiling: a 3px RIGHTWARD twitch on a column
 * measuring ~70 clamps to 128. That is direction-honest (`wider`, and it did
 * widen) and still commits a 58px jump for 3px of travel — the largest
 * unrequested jump this feature can now produce from the smallest gesture that
 * clears `DRAG_TOLERANCE_PX`. A LEFTWARD twitch there never reaches the
 * tolerance: `movesWrongWay` refuses it first, because `narrower` cannot land
 * above its own start.
 *
 * Flipping this example from the ceiling to the floor without flipping the
 * direction with it is how the paragraph came to describe a case its own test
 * does not exercise — that test drags RIGHT, 100 -> 104 (`/pr-review` round 8,
 * Agent C M3). The old upper case (a twitch on Name measuring ~1050 snapping to
 * 640) is unreachable now that nothing saturates from above.
 *
 * The honest statement of that defect is not "the start is out of range" — it
 * is "the column moved further than the pointer". So an amplified gesture has to
 * be worth at least one `STEP`: deliberately the same number, because one
 * keypress is the smallest deliberate resize this feature recognises, and the
 * pointer's threshold for the same amplified commit should not be smaller than
 * a keypress. If `STEP` moves, this moves with it.
 *
 * **NOT the fix that was rejected.** Requiring the gesture to actually REACH the
 * clamp would need 26 `ArrowLeft` presses to enter range from 1050 and turns the
 * "ArrowLeft still narrows a past-the-clamp column" test red — before the
 * ceiling's removal deleted that test and its arithmetic together. This asks for one
 * step of intent, not 410px of it.
 *
 * **Pointer-only, deliberately** — the same reason `DRAG_TOLERANCE_PX` is. There
 * is no keyboard jitter: every key is a discrete deliberate act, and `Home`/`End`
 * name a destination rather than a distance, so an amplification test would be
 * true for every `Home` press and would kill the key.
 */
const ADOPTION_TOLERANCE_PX = STEP;

/** The travel a gesture must clear before it counts, given what it would do.
 *
 *  **`Math.round(delta)`, not `delta`** — and the rounding is the whole point.
 *  `startWidth` is an integer (`beginResize` rounds the measured rect) and
 *  `clampWidth` rounds, so `target - startWidth` is `Math.round(delta)`. But
 *  `delta` is `clientX - startX`, and `clientX` is FRACTIONAL at any browser
 *  zoom other than 100%, and for trackpad, pen and touch input. Comparing a
 *  rounded distance against an unrounded one made every drag whose fractional
 *  part landed at .5 or above read as amplified: at `startWidth: 300` — dead
 *  centre of the range, nowhere near a clamp — a `-8.6px` drag produces
 *  `|291 - 300| = 9 > 8.6`, so the threshold jumped to 16px and the gesture
 *  was dropped with no paint, no commit and no warning. Roughly half of all
 *  3-16px drags, silently, and a REGRESSION against the behaviour before this
 *  guard existed (`/pr-review` #771 iter 1, Agent A F1).
 *
 *  Rounding both sides restores the docstring above to something true: with
 *  `startWidth` in range the clamp can only ever move the column LESS than the
 *  pointer (it truncates), never more, so amplification really is reachable
 *  only from a start BELOW `MIN_WIDTH`.
 *
 *  The tempting alternative — "amplified means the clamp changed the outcome",
 *  `clampWidth(start + delta) !== Math.round(start + delta)` — is WRONG, and
 *  measurably so: dragging a 140px column 14px narrower clamps 126 up to 128,
 *  which that predicate calls amplified. The column moved 12px while the
 *  pointer moved 14 — the OPPOSITE of amplified — and it would then demand
 *  16px of travel. It trades this bug for a new one at the other clamp. */
const toleranceFor = (startWidth: number, target: number, delta: number) =>
  Math.abs(target - startWidth) > Math.abs(Math.round(delta))
    ? ADOPTION_TOLERANCE_PX
    : DRAG_TOLERANCE_PX;

/**
 * Per-status INK for the write-outcome column ("Added to search" / "Added to
 * Thrive") — sub-task 11 of the 2026-09-04 epic: the cell renders the
 * SERVER-derived `review_status` and nothing else. Its predecessor
 * (`exportStateBadge` + the FE `skipReason` derivation) re-derived the state
 * from `export_status`/`contact_id_thrive`/`is_w2` on the client, and three
 * such derivations disagreeing is the bug `review_status` exists to end.
 *
 * The WORDS live in `reviewStatusCopy.ts`, shared with the export panel so the
 * two surfaces cannot name one state two ways.
 *
 * The three states that read "Not added yet" share ONE neutral ink, because
 * they are one sentence in this column: nothing has been added for this search.
 * Their Thrive-side difference is the Thrive ID column's to tell, and the amber
 * "nobody has looked" signal moved there with it — spending a colour here to
 * split three identical words would be colour CARRYING meaning rather than
 * reinforcing it.
 *
 * Only the states that record a search-side event are DATED, at the call site
 * (`searchStatusDate`).
 */
const SEARCH_STATUS_INK: Record<NonNullable<ImportContactRow["review_status"]>, string> = {
  not_compared: "text-text-secondary",
  ignored: "text-text-muted",
  to_review: "text-warning-ink",
  failed: "text-error-ink",
  exported: "text-success-ink",
  already_in_thrive: "text-text-secondary",
  not_in_thrive: "text-text-secondary",
};

/**
 * Per-status GLYPH, beside the ink above so the cell's whole visual vocabulary
 * is one table. Mapping approved by the operator 2026-09-07; it partially
 * answers Trello #748's "3-state icon system in the Thrive ID column" — placed
 * in the STATUS cell instead, over all seven states rather than three, a
 * divergence the operator accepted knowingly.
 *
 * The icon NEVER stands alone: it is rendered inside the badge, in front of the
 * word, and is `aria-hidden` so the visible word remains the single accessible
 * name (same rule the ink follows — colour reinforces, never carries). Someone
 * who distinguishes neither colour nor glyph reads the identical sentence.
 *
 *  · `to_review`      AlertTriangle — the ONE actionable state, on the repo's
 *                     existing warning glyph (ColumnMappingStep, the install
 *                     notice) so "triangle" keeps meaning "you must decide".
 *  · `exported`       UserPlus — WE added it, to this search.
 *  · `ignored`        EyeOff — the recruiter chose not to see it. Not punitive:
 *                     no cross, no triangle.
 *  · `failed`         CircleX — a real error, the only cross in the table.
 *  · the three states reading "Not added yet" (`not_compared`,
 *                     `already_in_thrive`, `not_in_thrive`) share CircleDashed:
 *                     one word, one glyph. Giving them the three different
 *                     glyphs they used to carry (dashed circle / check / search
 *                     -with-cross) would make the icon say something the word
 *                     does not — the Thrive-side distinction, which is the
 *                     Thrive ID column's job now.
 */
const SEARCH_STATUS_ICON: Record<NonNullable<ImportContactRow["review_status"]>, LucideIcon> = {
  not_compared: CircleDashed,
  ignored: EyeOff,
  to_review: AlertTriangle,
  failed: CircleX,
  exported: UserPlus,
  already_in_thrive: CircleDashed,
  not_in_thrive: CircleDashed,
};

/** Icon geometry for anything rendered INSIDE a `text-caption` line. `1em`
 *  tracks the inherited `--font-size-caption` instead of restating it as a
 *  pixel count that a token change would silently desynchronize. */
const STATUS_ICON_CLASS = "size-[1em] shrink-0";

/**
 * Background of the two FROZEN body cells (checkbox + Name).
 *
 * OPAQUE, because rows scroll under them, and themed through one token:
 * `--table-cell-surface` is the page on dark (what these cells have always
 * painted) and the white sheet on light, where a bare `bg-bg` leaves two
 * page-grey columns on a white table.
 *
 * Not `bg-table-surface`: that is the WRAPPER's token and is transparent on
 * dark, so rows would show through. Not `bg-surface` either: that is #141414
 * on dark, where these cells sit on #0a0a0a. `tokens.css` says why the two
 * surfaces cannot share a token.
 */
const FROZEN_CELL_SURFACE_CLASS = "bg-table-cell-surface";
/** Right edge of the frozen Name column while the table is scrolled sideways. */
const FROZEN_EDGE_SHADOW_CLASS = "shadow-frozen-edge";

/**
 * The row's hover / open tint, painted on a FROZEN cell.
 *
 * The `tr` takes `bg-row-hover` on hover and while its person is open, and
 * every ordinary cell shows it because none of them has a fill of its own. The
 * two frozen cells do: `FROZEN_CELL_SURFACE_CLASS` is opaque on purpose, so the
 * row's fill never reached them and the mark used to stop at the Name column.
 * `--row-hover` cannot replace that surface either: on dark it is an alpha tint
 * (`--accent-muted`), and a translucent frozen cell lets the rows scrolling
 * under it show through (SOP pitfall 8).
 *
 * So the tint goes ON TOP of the surface, as a `::before` overlay: the opaque
 * surface below, the same tint the `tr` paints over it, and the cell's content
 * above both (`-z-10` inside the cell's own stacking context, which its
 * `sticky` + `z-10` establish). Revealed by the row's hover, or by focus
 * inside the row (keyboard parity with the `tr`'s own `focus-within`),
 * through the NAMED group `group/row` on the `tr` (a bare `group` would also
 * reveal every Tooltip in the row, which opens on `group-hover`), or held on
 * by `FROZEN_CELL_TINT_ON_CLASS` while the row's person is open.
 */
const FROZEN_CELL_TINT_CLASS =
  "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-row-hover before:opacity-0 group-hover/row:before:opacity-100 group-focus-within/row:before:opacity-100";
const FROZEN_CELL_TINT_ON_CLASS = "before:opacity-100";
/**
 * The open row's accent mark: a stripe down the left edge of the row's FIRST
 * frozen cell. Not a ring on the `tr` — `box-shadow` on a table row is not
 * painted consistently across browsers, and the opaque frozen cells would
 * cover its left side anyway.
 */
const FROZEN_CELL_STRIPE_CLASS =
  "after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-0.5 after:bg-accent";

/**
 * A dated status line: "Sep 7, 10:04 AM".
 *
 * DATE AND TIME, in BOTH columns (operator, 2026-09-08 hand-testing). The
 * Thrive ID column used to drop the clock on the grounds that it is narrow and
 * "the minute does not change the decision" — but the date now sits on its OWN
 * line rather than trailing the word, so the width argument is gone, and the
 * minute does change one thing the operator cares about: whether two compare
 * runs minutes apart both reached a row. Older than this year drops the clock
 * and gains the year, which is the useful precision by then.
 *
 * ALWAYS RENDERED GREY, at every call site. The red/amber/green freshness scale
 * this replaced (`dateFreshness`) had colour carrying the message "this reading
 * of Thrive may have aged out" on its own, which is the rule this PR set when
 * it added the status icons: colour reinforces, never carries. The staleness
 * signal survives in WORDS, in the pre-compare confirmation's coverage note
 * ("N compared more than a week ago"), which is also the only place it can
 * prompt the action it implies.
 */
function formatStatusDate(at: Date, now: Date): string {
  if (at.getFullYear() !== now.getFullYear()) {
    return at.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  return at.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * WHICH timestamp dates a search-side status, or `undefined` when the state
 * records no search-side event at all.
 *
 * Only three states do: the WRITE (`exported_at`) under Added and Failed, and
 * the DISMISSAL (`left_out_at`) under Ignored. The fourth timestamp a row
 * carries — the compare VERDICT (`compared_at`) — dates the Thrive ID column
 * now, which is the column whose question it answers; it used to be this cell's
 * default and its fallback, and under a word like "Added" it reported the day
 * someone looked at Thrive as though it were the day we wrote to it.
 *
 * `to_review` and the three "Not added yet" states return nothing on purpose.
 * Nothing has happened to them on the search side, so there is no event here to
 * date — their date, where they have one, is a compare verdict and renders one
 * column to the right.
 *
 * A `failed` row often has no `exported_at`: `_record_contact_failure`
 * (`export_worker/service.py`) does not stamp it, so only a re-export that
 * failed after an earlier success carries one. That is the missing-date case,
 * not a reason to borrow the compare date.
 */
function searchStatusDate(row: ImportContactRow): string | null | undefined {
  switch (row.review_status) {
    case "ignored":
      return row.left_out_at;
    case "exported":
    case "failed":
      return row.exported_at;
    default:
      return undefined;
  }
}

/** Whether a compare has ever CLASSIFIED this row — the per-row compare fact,
 *  read exactly as the server reads it (`is_stale_compared`: both compare
 *  columns NULL means never compared; a legacy pre-0040 verdict has a
 *  `thrive_match_type` and no date, and IS compared).
 *
 *  A PRESENCE read for choosing which sentence the Thrive ID cell can state
 *  truthfully, in the same class as `isInThrive` — never a status derivation.
 *  It replaces the import-level `compareHasRun` in that cell: on a partially
 *  compared import that prop said "Not found" for every unmatched row,
 *  including the ones no compare had reached. */
function hasCompareVerdict(row: ImportContactRow): boolean {
  return row.compared_at != null || row.thrive_match_type != null;
}

/**
 * The "Added to search" cell: what happened to this contact IN THIS SEARCH, the
 * orthogonal duplicate-LinkedIn marker, and the inline Undo on Ignored rows.
 *
 * The column answers ONE question, and every state it can show answers that one
 * (2026-09-08 restructure, operator hand-testing): Added / Failed / Ignored /
 * To review, and "Not added yet" for the three states where nothing has been
 * added yet. What it no longer shows is Thrive-side vocabulary — "Already in
 * Thrive", "Not found", "Not compared yet" all sat here saying nothing about
 * whether the contact was added to THIS search, which is what the header asks.
 * They moved one column right, where the header does ask them.
 *
 *  - The date sits INLINE after the word, separated by "·", and is grey in
 *    every state (see `formatStatusDate`). It dates a search-side event only —
 *    the write under Added / Failed, the dismissal under Ignored. The compare
 *    verdict's date is in the Thrive ID cell.
 *  - The Undo button calls the existing DELETE .../leave-out through the
 *    parent (the same API the modal's per-card Undo uses); the row leaves the
 *    Ignored state on the refetch, which keeps this cell, the chips and the
 *    modal tabs reading one payload.
 *  - `data-testid="contact-export-status"` + `data-status` survive as the
 *    pre-existing E2E hooks, and `data-status` still carries the raw
 *    `review_status` — UNCHANGED by this restructure. Three server states now
 *    render the same WORD here, so the attribute is strictly more precise than
 *    the visible text: a spec asserting `already_in_thrive` still gets it,
 *    while a spec asserting the words "Already in Thrive" is now asserting a
 *    sentence this column deliberately no longer says.
 */
/** The stage every export creates a candidacy at (`export_worker`'s
 *  `_IDENTIFIED_STAGE_ID`). */
const IDENTIFIED_STAGE = "Identified";

function SearchStatusCellContent({
  row,
  onUndoLeaveOut,
  onUndoDifferentPerson,
  identifiedOnAdded = false,
}: {
  row: ImportContactRow;
  onUndoLeaveOut?: (contactId: string) => void;
  onUndoDifferentPerson?: (contactId: string, thriveId: string) => void;
  /**
   * A raw Thrive pull (Raw Import · Thrive) only: an added row also shows the
   * stage it got in THIS search, "Identified" (every export creates the
   * candidacy there; Jared, 2026-10-20). Its Origin stage column describes
   * another search, so the pill keeps the two apart. Elsewhere "Added" already
   * implies it, and the pill would only repeat it.
   */
  identifiedOnAdded?: boolean;
}) {
  const status = row.review_status;
  const now = new Date();
  // The search-side word plus, on `to_review`, the qualifier naming WHICH
  // compare finding the row is waiting on. Shared with the export panel; a
  // display detail read off `is_w2`/`is_w3`/`thrive_match_type`, never a second
  // status derivation — the status itself is the server's.
  const label = searchStatusLabel(row);
  const dateAt = searchStatusDate(row);
  const date = dateAt ? new Date(dateAt) : null;
  // `undefined` = this state records no search-side event, so there is no date
  // to miss. `null` = it records one and the timestamp is absent, which the
  // cell has to SAY: silence reads exactly like the blank-status bug this epic
  // removed (the 1,777-contact pre-0040 import the operator hand-tested).
  const dateIsExpected = dateAt !== undefined;
  const StatusIcon = SEARCH_STATUS_ICON[status];
  const badge = (
    <span
      data-testid="contact-export-status"
      data-status={status}
      data-updated={wasUpdated(row) ? "true" : undefined}
      className={`inline-flex items-center gap-1 text-caption ${SEARCH_STATUS_INK[status]}`}
    >
      <StatusIcon aria-hidden="true" className={STATUS_ICON_CLASS} />
      {label}
    </span>
  );
  // The date gets its OWN LINE (operator, 2026-09-08). Inline after a "·" it
  // shared a row with a status word that can already run long — "To review —
  // identity mismatch" — so the date wrapped, or pushed the column wider, and
  // in either case read as a continuation of the sentence rather than as a
  // record of when. The separator constant goes with it: a line of its own
  // needs no separator.
  const dateLine = dateIsExpected ? (
    <span data-testid="search-status-date" className="text-caption text-text-secondary">
      {date ? formatStatusDate(date, now) : DATE_NOT_RECORDED}
    </span>
  ) : null;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-2">
        {status === "failed" && row.export_error ? (
          // `escapesOverflow`: this row sits inside the table's
          // `overflow-auto` box, which clips an absolutely-positioned bubble at
          // its edge. Opens upward (`side` default), so the rows it fails on
          // are the FIRST visible ones rather than the last.
          // `clickToPin` like every other note in this view (operator,
          // 2026-09-08: "que todos los tooltips dentro de people list detail
          // tengan la misma construcción"). An export error is the one a
          // recruiter most wants to keep on screen while they read it, and
          // hover-only made that the hardest to do.
          <Tooltip content={row.export_error} escapesOverflow clickToPin>
            {badge}
          </Tooltip>
        ) : (
          badge
        )}
        {identifiedOnAdded && status === "exported" ? (
          <span data-testid="contact-added-stage">
            <StagePill stage={IDENTIFIED_STAGE} />
          </span>
        ) : null}
        {status === "ignored" && onUndoLeaveOut && (
          <button
            type="button"
            data-testid={`contacts-undo-leave-out-${row.import_contact_id}`}
            className="rounded-md border border-border bg-bg px-2 py-0.5 text-label text-text hover:bg-surface"
            onClick={() => onUndoLeaveOut(row.import_contact_id)}
          >
            Undo
          </button>
        )}
        {/* The ⓘ on the W1 qualifier — the OTHER duplicate state, and the one
            whose wording ("already duplicated in Thrive") the operator misread
            as its near-opposite below. The note says which of the two facts
            this is and where it gets fixed; the words alone had to carry that
            and could not do it in the width of a table cell. */}
        {status === "to_review" && row.thrive_match_type === "duplicate" && (
          <InfoHint
            content={ALREADY_DUPLICATED_HINT}
            label="About this duplicate"
            escapesOverflow
          />
        )}
      </span>
      {dateLine}
      {row.duplicate_linkedin && (
        <span
          data-testid={`row-dup-warning-${row.import_contact_id}`}
          className="inline-flex items-center gap-1 text-caption text-warning-ink"
        >
          {/* `Copy`, NOT a second triangle: this marker sits on the same row as
              a status icon, and two triangles side by side would read as one
              escalated warning instead of two independent facts. The word
              carries the meaning either way.

              The marker closes on "will duplicate on export" — a write we have
              NOT made yet — and OPENS on the cause, which is the half it was
              missing: a fresh file showing it read as a bug until the row said
              a decision had been made. Its
              near-opposite, the W1 qualifier ("already duplicated in Thrive"),
              is one line up in this same cell on a to_review row, and the two
              used to read almost identically. */}
          <Copy aria-hidden="true" className={STATUS_ICON_CLASS} />
          {WILL_DUPLICATE_MARKER}
          <InfoHint
            content={
              <span className="flex flex-col gap-1.5">
                <span>{WILL_DUPLICATE_HINT.what}</span>
                <span>{WILL_DUPLICATE_HINT.whyAlready}</span>
              </span>
            }
            label="About this warning"
            escapesOverflow
          />
          {/* THE REVERSAL SURFACE, and the only one that is always reachable.
              The decision's Undo used to live exclusively on the Compare
              modal's same-URL card — a card that is not guaranteed to be
              there. A pair recorded from a NAME match sits on a URL no Thrive
              contact holds, so the run reports it unheld and the card is
              dropped; the pair survives (a zero observation may not delete
              one) and the export keeps forcing a CREATE. No screen could
              reach it (/pr-review iter 1, Agent A, B2).

              This row already carries the warning, so it is where the
              correction belongs: the marker and its Undo describe one fact.

              ONE BUTTON PER PAIR, labelled with the Thrive id when there are
              several — the registry is firm-wide and a URL can carry decisions
              made by different people, so "undo" without saying which would be
              a bigger hammer than the card ever offered. Same vocabulary as
              `DifferentPersonCard`'s footer. */}
          {onUndoDifferentPerson &&
            (row.different_person_thrive_ids ?? []).map((thriveId) => (
              <button
                key={thriveId}
                type="button"
                data-testid={`contacts-undo-different-person-${row.import_contact_id}-${thriveId}`}
                className="rounded-md border border-border bg-bg px-2 py-0.5 text-label text-text hover:bg-surface"
                onClick={() => onUndoDifferentPerson(row.import_contact_id, thriveId)}
              >
                {(row.different_person_thrive_ids ?? []).length === 1
                  ? UNDO_DIFFERENT_PERSON_LABEL
                  : `${UNDO_DIFFERENT_PERSON_LABEL} (#${thriveId})`}
              </button>
            ))}
        </span>
      )}
    </span>
  );
}

/**
 * `true` when an "exported" row updated an ALREADY-EXISTING Thrive contact
 * rather than creating a new one. Nothing RENDERS this any more — it feeds
 * `data-updated` only, and is the sole surviving carrier of match-vs-create
 * after the visible "Updated" line was dropped. `thrive_match_type` is Compare-time
 * classification, not a separate export-time field — but it's the
 * authoritative signal for which path Export took (`new` ⇒
 * `_create_with_latest_position`; anything else ⇒ `_update_contact`, see
 * export_worker/service.py), so no backend change is needed to derive this
 * for the common case.
 *
 * KNOWN GAP (PR #479 review, Agent C): a contact whose export previously
 * partially failed (Thrive contact created, but the run didn't reach
 * `exported` — e.g. a subsequent candidacy-create failure) is resumed as an
 * UPDATE on retry regardless of its ORIGINAL `thrive_match_type`
 * (`resolve_export_plan`'s resumability rule 2b in
 * `nu_assist_shared/domain/export.py` — once a `contact_id_thrive` exists,
 * every later export write is an update, never a re-create). `thrive_match_type`
 * is fixed at Compare time and never updated to reflect that, so THIS specific
 * resume-after-partial-failure case reports `data-updated` ABSENT when the
 * actual write was an update. The gap is unchanged; only its symptom moved.
 * It used to be described as the row showing a plain "Exported" with no
 * "Updated" label — neither exists now: the badge is `✓` with the accessible
 * name "Added", and no row carries an "Updated" label at all (`/pr-review`
 * #771 iter 3, Agents A + C). Correctly distinguishing it would need a
 * dedicated export-time outcome field from the backend (not yet built) —
 * narrow edge case, not fixed in this PR.
 */
function wasUpdated(row: ImportContactRow): boolean {
  return (
    row.export_status === "exported" &&
    row.thrive_match_type != null &&
    row.thrive_match_type !== "new"
  );
}

export interface ContactsTableSort {
  key: string;
  dir: "asc" | "desc";
}

/** Reserved sort key for the "Name" column — a sentinel (no criterion is named
 *  this) so the same `sort`/`onSort` mechanism the annex/score columns use can
 *  also sort contacts alphabetically by name. */
export const NAME_SORT_KEY = "__name";

/** Shared no-op for the props only the detail surface supplies. One frozen
 *  identity rather than a fresh `() => {}` per render, so a memoized child
 *  never re-renders on a default that never changes. */
const noop = () => {};

/** The "nothing is selected" identity, shared for the same reason as `noop`. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();

/** Sort key for the "Contact info" (email) column. */
export const EMAIL_SORT_KEY = "__email";

/** Sort keys for the Thrive candidacy columns of a Thrive-sourced list (epic
 *  2026-10-15-thrive-search-import, 04): the table's sentinel → the API's
 *  `sort_key`. Stage sorts along the recruiting ladder server-side. */
export const STAGE_SORT_KEY = "__stage";
export const IN_STAGE_SORT_KEY = "__stage_since";
export const ADDED_SORT_KEY = "__added_at";
export const THRIVE_COLUMN_SORT_KEYS: Readonly<Record<string, string>> = {
  [STAGE_SORT_KEY]: "stage",
  [IN_STAGE_SORT_KEY]: "stage_since",
  [ADDED_SORT_KEY]: "added_at",
};

/** What the three Thrive columns need from the list (04). Present only on a
 *  list whose rows carry Thrive candidacy data — pulled from a Thrive search,
 *  or derived from one (05); its absence renders today's table. */
export interface ThriveColumnsContext {
  /** The list's `created_at`: days in stage are counted up to it, so the
   *  value is frozen rather than growing with today. On a derived list that
   *  is the derived list's own date, not the pull's. */
  importedAt: string;
  /** The SOURCE search the stages describe — not the linked (target) one. */
  originSearchId: string | null;
  originSearchName: string | null;
  /** A list DERIVED from a pull (Combine & Filter, AI Review, Company Map):
   *  its rows may come from several Thrive searches, so the columns name
   *  none (05). */
  derived?: boolean;
}

/** `search 5004 · Acme CTO`, the source the three columns describe; "its
 *  Thrive search" on a derived list, which may hold several. */
export function originSearchPhrase(ctx: ThriveColumnsContext): string {
  if (ctx.derived) return "its Thrive search";
  if (!ctx.originSearchId) return "the source search";
  const name = ctx.originSearchName?.trim();
  return name ? `search ${ctx.originSearchId} · ${name}` : `search ${ctx.originSearchId}`;
}

/** When "Stage" (`"stage"`) and "In stage" (`"days"`) were read: the pull.
 *  On a derived list the stage is still the pull's, but the days count to
 *  that list's own date (`importedAt`). */
function asOfPhrase(ctx: ThriveColumnsContext, column: "stage" | "days"): string {
  if (!ctx.derived) return "as of the import date";
  return column === "stage"
    ? "as of the pull it came from"
    : "counted to the date this list was made";
}

/** A row's photo (05: chosen per row, not per list). A stored LinkedIn avatar
 *  wins (`profile_image_s3_key`, presigned in `resolvedAvatars`); a row
 *  without one, on a list whose rows carry Thrive candidacy data, falls back
 *  to the Thrive photo of the contact it was pulled from
 *  (`thrive_source_contact_id` — Compare can null `contact_id_thrive`, never
 *  this one). So ChopLink rows of a combined list, and people in both kinds of
 *  list, keep their LinkedIn photo. `undefined` renders initials. */
export function rowAvatarUrl(
  row: Pick<ImportContactRow, "profile_image_s3_key" | "thrive_source_contact_id">,
  resolvedAvatars: ReadonlyMap<string, string> | undefined,
  thriveAvatars: ReadonlyMap<string, string> | undefined
): string | undefined {
  if (row.profile_image_s3_key) return resolvedAvatars?.get(row.profile_image_s3_key);
  if (thriveAvatars && row.thrive_source_contact_id) {
    return thriveAvatars.get(row.thrive_source_contact_id);
  }
  return undefined;
}

/** Format a BCT year range for display.
 * Both null → "—" (no date data at all).
 * Null start with non-null end → "?–YYYY" (end known, start unknown).
 * Null end → "present". */
function formatYears(start: number | null | undefined, end: number | null | undefined): string {
  if (start == null && end == null) return "—";
  if (start == null) return `?–${end}`;
  return `${start}–${end ?? "present"}`;
}

interface ResizeHandlers {
  begin: (
    key: string,
    fallback: number | undefined,
    isFill: boolean
  ) => (e: React.PointerEvent<HTMLSpanElement>) => void;
  move: (e: React.PointerEvent<HTMLSpanElement>) => void;
  end: (e: React.PointerEvent<HTMLSpanElement>) => void;
  cancel: (e: React.PointerEvent<HTMLSpanElement>) => void;
  reset: (key: string) => void;
  key: (key: string, fallback?: number) => (e: React.KeyboardEvent<HTMLSpanElement>) => void;
}

/**
 * The right-edge grab strip. Every column header carries one (checklist 7);
 * before this it existed only on the dynamic annex columns — i.e. on none of the
 * columns a Raw Import or a Company Map list actually shows.
 *
 * **Declared at module scope on purpose.** As a function defined inside
 * `ContactsTable` it was a NEW component type on every render, so React
 * unmounted and remounted the strip each time — which destroys the DOM node the
 * gesture is attached to. A keyboard resize worked exactly once and then went
 * silent (the second ArrowLeft landed on a detached node), and a drag would drop
 * focus mid-gesture. The handlers ride in as props instead.
 *
 * `aria-valuenow` is omitted while **React renders no width for the column** —
 * which is NOT the same as "no override", and an earlier revision of this
 * paragraph said the latter (`/pr-review` iter 8, Agent C). Contact info and the
 * annex columns supply a call-site fallback, so they are React-pinned and DO
 * report a value with no override of their own; only the six columns that pass
 * no fallback go quiet. The rule is the same `reactPinsWidth` distinction
 * `beginResize` reasons about.
 *
 * It is deliberate where it applies: there is no width of OURS to report, and
 * asserting the browser's current auto width would be a number that changes
 * under the reader's feet on every re-layout. It appears the moment a resize
 * commits.
 */
/**
 * Upper bound ANNOUNCED to assistive tech, in px. Not a clamp — `clampWidth`
 * has no ceiling and a drag can pass this — purely so a focusable `separator`
 * reports a coherent `[valuemin, valuemax]`. Sized well past any width a real
 * column reaches so it never truncates the announcement in practice.
 */
const ARIA_MAX_WIDTH = 4000;

function ColumnResizeHandle({
  columnKey,
  label,
  width,
  isFill = false,
  handlers,
}: {
  columnKey: string;
  label: string;
  width?: number;
  /** Whether this column absorbs the table's surplus. Changes only what the
   *  grip is CALLED — on a fill column the gesture moves a floor, not a width. */
  isFill?: boolean;
  handlers: ResizeHandlers;
}) {
  return (
    /* The rule below wants `<hr>` for `role="separator"`. That suggestion is a
       THEMATIC BREAK; this is the ARIA window-splitter pattern — a FOCUSABLE
       separator carrying `aria-valuenow` / `-valuemin` / `-valuemax`, which is
       how a screen reader learns the column's current width and that arrows
       change it. `<hr>` is not focusable, has no value semantics, and is not
       valid inside a `<th>` alongside the header's own label and sort button.

       The rule started firing only when this span gained a child (the
       rest-state rule); the childless version it replaced was identical in role
       and ARIA, so the suppression documents a constraint that was always
       here. */
    // biome-ignore lint/a11y/useSemanticElements: focusable valued separator, not a thematic break — see above
    <span
      role="separator"
      aria-orientation="vertical"
      // The label says what the control CHANGES. On the fill column a gesture
      // moves the floor, not the width — the column paints `width: 100%` and
      // can be far wider than the value announced — so calling it "Resize"
      // there would name the wrong quantity (`/pr-review` round 11, Agent A).
      aria-label={isFill ? `Minimum width for ${label} column` : `Resize ${label} column`}
      // ALL THREE VALUE ATTRIBUTES, OR NONE. A focusable `separator` that
      // announces `valuemin` and `valuemax` with no `valuenow` describes a
      // range around nothing, which is what the five fluid columns did.
      //
      // The asymmetry with `aria-valuemax` is real and is why this is a
      // decision rather than an oversight: ARIA DEFINES a default of 100 for
      // `aria-valuemax`, so omitting THAT one fabricated a number (and one
      // below our own declared minimum). MDN is explicit that a missing
      // `aria-valuenow` implies nothing at all — so omitting it invents no
      // value, and "this column has no width of ours" is simply true until
      // someone resizes it. Keeping the omission and dropping the empty range
      // with it is the coherent form.
      //
      // SETTLED, after a reviewer flagged at ~80% that a focusable `separator`
      // might REQUIRE `aria-valuenow` — which would have made this trade one
      // violation for another — and then refuted his own flag (`/pr-review`
      // rounds 12-13, Agent C). MDN's role reference conditions it: include
      // `aria-valuenow` "if the separator is focusable and has a KNOWN value",
      // and "if not focusable or the value is unknown, do not include this
      // attribute". A column with no override is exactly that carve-out. The
      // same source prescribes `valuemin`/`valuemax` on the mirror rule —
      // include them when they are not the 0/100 defaults, which 128 and 4000
      // are not.
      //
      // The APG window-splitter pattern does state all three flatly, and that
      // is not a contradiction: a splitter always HAS a position, so the
      // unknown-value carve-out never applies to it. Our fluid columns are the
      // carve-out. Caveat kept because it is the honest one: the reviewer could
      // not retrieve the normative role-characteristics table (the spec pages
      // truncate before it), so this cites MDN and the APG, not ARIA itself.
      {...(width == null
        ? {}
        : {
            "aria-valuenow": width,
            "aria-valuemin": MIN_WIDTH,
            "aria-valuemax": Math.max(ARIA_MAX_WIDTH, width),
          })}
      data-testid="column-resize-handle"
      data-key={columnKey}
      tabIndex={0}
      onPointerDown={handlers.begin(columnKey, width, isFill)}
      onPointerMove={handlers.move}
      onPointerUp={handlers.end}
      onPointerCancel={handlers.cancel}
      onDoubleClick={() => handlers.reset(columnKey)}
      onKeyDown={handlers.key(columnKey, width)}
      // TWO ELEMENTS, TWO JOBS. The outer span is the TARGET: 8px wide
      // (`w-2`, up from `w-1.5`), transparent, and the only thing carrying
      // pointer/keyboard handlers. The inner span is the CUE: a 1px rule that
      // is always painted, so the column boundary is visible before anyone
      // hovers anything.
      //
      // The cue is drawn by this span rather than by a `border-r` on the `<th>`
      // for the same reason the header divider is a `shadow-[inset_...]` and
      // not a `border-b`: the table is `border-collapse`, which drops a sticky
      // cell's own border once it scrolls — and `name` IS sticky. A border here
      // would vanish on exactly the column a recruiter grabs most.
      className="group absolute right-0 top-0 flex h-full w-2 cursor-col-resize touch-none select-none justify-end bg-transparent focus-visible:outline-none"
    >
      {/* Rest state is `bg-border` — the same token the table's own rules use,
          so an untouched header reads as "this column has an edge", not as
          "something is highlighted here". Hover and keyboard focus promote it
          to 2px `bg-accent`, which is the affordance the old handle only ever
          showed on hover (it was `bg-transparent` at rest, i.e. invisible).
          Only the colour transitions: animating the width makes the rule
          appear to drift away from the boundary it marks.

          CALIBRATION, so nobody overstates this later: at rest the rule is
          `rgba(255, 255, 255, 0.14)`, roughly 1.2:1 against the header
          surface. That is a hairline that MARKS the boundary; it is not a
          control shouting for attention, and `cursor-col-resize` over the 8px
          target still does much of the discovery work. Deliberate — a subtle
          always-on divider was the requested design over a grip icon — but
          "the boundary is marked" is the honest claim, not "you can't miss
          it". `aria-hidden` here is belt-and-braces: `role="separator"` already
          takes presentational children, so the rule is out of the a11y tree
          either way. Both the unit test and the e2e select on it, so it stays. */}
      <span
        aria-hidden="true"
        className="h-full w-px bg-border transition-colors group-hover:w-0.5 group-hover:bg-accent group-focus-visible:w-0.5 group-focus-visible:bg-accent"
      />
    </span>
  );
}

/**
 * A header of the three Thrive candidacy columns (04): the sort toggle the
 * Name and Contact info headers use, an ⓘ naming the SOURCE search the column
 * describes (a list pulled from one search may be linked to another, and the
 * search card names that one), and the resize grip every column carries.
 * Module scope for the same reason as `ColumnResizeHandle`.
 */
function ThriveColumnHeader({
  columnKey,
  label,
  sortKey,
  hint,
  sort,
  onSort,
  style,
  width,
  isFill = false,
  handlers,
}: {
  columnKey: string;
  label: string;
  sortKey: string;
  hint: string;
  sort?: ContactsTableSort | null;
  onSort?: (key: string) => void;
  style?: React.CSSProperties;
  width?: number;
  isFill?: boolean;
  handlers: ResizeHandlers;
}) {
  const active = sort?.key === sortKey;
  return (
    <th
      data-testid={`${columnKey}-header`}
      className="relative px-3 py-2"
      style={style}
      aria-sort={
        active ? (sort?.dir === "asc" ? "ascending" : "descending") : onSort ? "none" : undefined
      }
    >
      <span className="flex items-center gap-1 whitespace-nowrap">
        {onSort ? (
          <button
            type="button"
            data-testid={`${columnKey}-sort`}
            className="flex items-center gap-1 hover:text-text"
            onClick={() => onSort(sortKey)}
          >
            {label}
            <span aria-hidden="true" className={active ? "text-accent" : "text-text-muted"}>
              {active ? (sort?.dir === "desc" ? "▼" : "▲") : "↕"}
            </span>
          </button>
        ) : (
          label
        )}
        <InfoHint content={hint} label={`About the ${label} column`} escapesOverflow />
      </span>
      <ColumnResizeHandle
        columnKey={columnKey}
        label={label}
        width={width}
        isFill={isFill}
        handlers={handlers}
      />
    </th>
  );
}

/**
 * The props whose REQUIREDNESS depends on which surface is rendering.
 *
 * They were briefly optional-with-defaults so the AI Review preview did not
 * have to fabricate ~20 props it renders nothing for. That was too blunt: it
 * removed the compiler's ability to hold the DETAIL page to its own
 * requirements, and two concrete regressions became type-clean. Omit
 * `compareHasRun` and it defaults falsy, so every unlinked row claims "Compare
 * to Thrive has not been run" on an import that WAS compared — the exact
 * distinction cl.4 item 13 added the prop for. Omit `importId` and entry 0 of
 * the measurement signature silently disappears, re-opening the import->import
 * width-inheritance latch its own docstring spends twenty lines on.
 *
 * The union restores enforcement without putting the fabrication back: the
 * detail surface (the default) must supply all of them; the preview may supply
 * none. That is ALL of them — an earlier revision listed only the six most
 * dangerous and left the eighteen filter props optional on both arms, so
 * `ImportDetailPage` could drop `onNameQueryChange` and type-check while the
 * search box rendered, called `noop`, and filtered nothing forever. Closing
 * the shape for six props and leaving it open for eighteen is not closing it. `variant` is the discriminant, so the compiler picks the arm.
 */
type ContactsTableSurfaceProps =
  | {
      variant?: "detail";
      selectedIds: ReadonlySet<string>;
      /** Row checkbox. The second argument is the CURRENT page's selectable
       *  ids — in `all-filtered` mode the parent's selection model uses it to
       *  drop to an explicit selection (visible minus the unchecked row); in
       *  explicit mode it is ignored. See `useContactsSelection`. */
      onToggleSelect: (id: string, visibleSelectableIds: string[]) => void;
      onToggleSelectAll: (ids: string[], select: boolean) => void;
      /** `explicit` | `all-filtered` — drives the banner + the checked state
       *  of every checkbox (all-filtered means everything matching the active
       *  filters is selected). */
      selectionMode: ContactsSelectionMode;
      /** Server `total` for the ACTIVE filters (the whole filtered set, not
       *  the page). The banner's N. `undefined` while loading — no banner. */
      totalFilteredContacts: number | undefined;
      /** Escalate: "Select all N contacts" in the banner. */
      onSelectAllFiltered: () => void;
      /** De-escalate: "Clear selection" in the all-filtered banner. */
      onClearCrossPageSelection: () => void;
      importId: string;
      rowsArePlaceholder: boolean;
      compareHasRun: boolean;
      nameQuery: string;
      onNameQueryChange: (value: string) => void;
      reviewStatusFilter: ReviewStatusFilter;
      onReviewStatusFilterChange: (value: ReviewStatusFilter) => void;
      /** The orthogonal ⚠ Dup. LinkedIn warning chip (composes with the
       *  status chip — a warning, never a status). */
      dupLinkedinFilter: boolean;
      onDupLinkedinFilterChange: (value: boolean) => void;
      /** fu5 sub-task 01 — the second state axis and the orthogonal mark.
       *  Optional in BOTH prop shapes: a surface that has not adopted the
       *  split still renders a working bar without them. */
      thriveIdFilter?: ThriveIdFilter;
      onThriveIdFilterChange?: (value: ThriveIdFilter) => void;
      addToSearchFilter?: AddToSearchFilter;
      onAddToSearchFilterChange?: (value: AddToSearchFilter) => void;
      skippedFilter?: boolean | null;
      onSkippedFilterChange?: (value: boolean | null) => void;
      /** Inline Undo on an Ignored row — the parent calls the existing
       *  DELETE .../leave-out and refetches. Required on the detail surface
       *  for the same reason the other callbacks are: omit it and every
       *  Ignored row silently loses its Undo. */
      onUndoLeaveOut: (contactId: string) => void;
      /** Retire one firm-wide "not the same person" pair. Same shape as
       *  `onUndoLeaveOut`: the table reports, the page owns the call. */
      onUndoDifferentPerson?: (contactId: string, thriveId: string) => void;
      enrichmentFilter: EnrichmentFilter;
      onEnrichmentFilterChange: (value: EnrichmentFilter) => void;
      currentPositionFilter: string;
      onCurrentPositionFilterChange: (value: string) => void;
      currentCompanyFilter: string;
      onCurrentCompanyFilterChange: (value: string) => void;
      bctCompanyFilter: string;
      onBctCompanyFilterChange: (value: string) => void;
      bctPositionFilter: string;
      onBctPositionFilterChange: (value: string) => void;
      bctYearFrom: string;
      onBctYearFromChange: (value: string) => void;
      bctYearTo: string;
      onBctYearToChange: (value: string) => void;
    }
  | {
      variant: "preview";
      selectedIds?: ReadonlySet<string>;
      onToggleSelect?: (id: string, visibleSelectableIds: string[]) => void;
      onToggleSelectAll?: (ids: string[], select: boolean) => void;
      selectionMode?: ContactsSelectionMode;
      totalFilteredContacts?: number | undefined;
      onSelectAllFiltered?: () => void;
      onClearCrossPageSelection?: () => void;
      importId?: string;
      rowsArePlaceholder?: boolean;
      compareHasRun?: boolean;
      nameQuery?: string;
      onNameQueryChange?: (value: string) => void;
      reviewStatusFilter?: ReviewStatusFilter;
      onReviewStatusFilterChange?: (value: ReviewStatusFilter) => void;
      dupLinkedinFilter?: boolean;
      onDupLinkedinFilterChange?: (value: boolean) => void;
      thriveIdFilter?: ThriveIdFilter;
      onThriveIdFilterChange?: (value: ThriveIdFilter) => void;
      addToSearchFilter?: AddToSearchFilter;
      onAddToSearchFilterChange?: (value: AddToSearchFilter) => void;
      skippedFilter?: boolean | null;
      onSkippedFilterChange?: (value: boolean | null) => void;
      onUndoLeaveOut?: (contactId: string) => void;
      /** Retire one firm-wide "not the same person" pair. Same shape as
       *  `onUndoLeaveOut`: the table reports, the page owns the call. */
      onUndoDifferentPerson?: (contactId: string, thriveId: string) => void;
      enrichmentFilter?: EnrichmentFilter;
      onEnrichmentFilterChange?: (value: EnrichmentFilter) => void;
      currentPositionFilter?: string;
      onCurrentPositionFilterChange?: (value: string) => void;
      currentCompanyFilter?: string;
      onCurrentCompanyFilterChange?: (value: string) => void;
      bctCompanyFilter?: string;
      onBctCompanyFilterChange?: (value: string) => void;
      bctPositionFilter?: string;
      onBctPositionFilterChange?: (value: string) => void;
      bctYearFrom?: string;
      onBctYearFromChange?: (value: string) => void;
      bctYearTo?: string;
      onBctYearToChange?: (value: string) => void;
    };

/** Why a row does not open the side panel: no URL at all… */
export const NO_LINKEDIN_PROFILE = "No LinkedIn profile — can't open the side panel";
/** …or a LinkedIn link that is not a profile (`/sales/lead/…`, `/pub/…`). */
export const NOT_A_PROFILE_URL = "Not a LinkedIn profile link (/in/…) — can't open the side panel";

/**
 * The ONE place that decides whether a row "has a LinkedIn URL": the trimmed
 * value, or `null` for a missing or whitespace-only one. The chip and the
 * icon both read it, so a whitespace-only URL can no longer render an icon
 * beside a "no LinkedIn" chip (two guards used to disagree on it).
 */
function linkedinUrlOf(row: ImportContactRow): string | null {
  const url = row.linkedin_url?.trim();
  return url ? url : null;
}

/**
 * The row button's accessible name: the intent ("open … in the side panel")
 * around what the button visibly shows — the name and, when there is one,
 * the same `<company>, <role>` line `CurrentPositionLine` renders. Without
 * the line a screen reader would hear less than a sighted user sees.
 */
function rowEntryLabel(row: ImportContactRow): string {
  const line = currentPositionLine(row);
  return line
    ? `Open ${fullName(row)}, ${line}, in the side panel`
    : `Open ${fullName(row)} in the side panel`;
}

/**
 * The Name cell's content box: the avatar beside the name and position line,
 * filling the cell so that the row button (or its inert twin) spans the whole
 * bounding box of what it describes. `text-left` because a `<button>` centres
 * its text by default. Always a `<span>`: a `<button>` allows phrasing
 * content only, so a `<div>` inside it is invalid HTML.
 */
const ROW_ENTRY_LAYOUT_CLASS = "flex min-w-0 flex-1 items-center gap-2 text-left";

/**
 * The person side panel's entry point: ONE button spanning avatar, name and
 * position line (row-CTA epic, sub-task 02). Until then only the avatar was
 * the button, with a small badge that recruiters did not read as a control
 * (Jose, 2026-09-28).
 *
 * Three shapes, one layout:
 * - the entry is off (no `onOpen`: the AI Review and Combine & Filter
 *   previews) — a plain box, nothing to click;
 * - the row has no `/in/` handle — an inert, `cursor-not-allowed` box with
 *   the avatar dimmed and a chip that says why, in the tooltip copy the
 *   avatar used to carry on its own;
 * - otherwise the button, with the panel's contract: `aria-haspopup`,
 *   `aria-expanded`, and `data-person-avatar` + `data-person-row`, which the
 *   host's focus return queries by handle so it survives the rows
 *   re-rendering. The attribute keeps its historical name.
 *
 * The button has NO hover of its own: hover and the open state are painted
 * on the whole `tr`, and the frozen cells follow through their overlay.
 */
function PersonRowEntry({
  row,
  handle,
  open,
  onOpen,
  avatar,
  children,
}: {
  row: ImportContactRow;
  handle: string | null;
  open: boolean;
  onOpen?: (row: ImportContactRow, handle: string) => void;
  avatar: React.ReactNode;
  children: React.ReactNode;
}) {
  if (!onOpen)
    return (
      <span className={ROW_ENTRY_LAYOUT_CLASS}>
        {avatar}
        {children}
      </span>
    );
  // The panel is keyed by the LinkedIn `/in/<handle>`: a row without one (a
  // CSV with no URL column, a ChopLink capture whose profile never loaded, or
  // a non-profile link such as Sales Navigator's `/sales/lead/…`) has no
  // person to open. Say so inline, instead of a row that silently does
  // nothing next to ones that open.
  if (handle === null) {
    const notAProfile = linkedinUrlOf(row) !== null;
    return (
      <span
        data-testid="person-row-no-linkedin"
        className={`${ROW_ENTRY_LAYOUT_CLASS} cursor-not-allowed`}
      >
        <span className="shrink-0 opacity-60">{avatar}</span>
        {children}
        <Tooltip
          content={notAProfile ? NOT_A_PROFILE_URL : NO_LINKEDIN_PROFILE}
          escapesOverflow
          // Below, not above: the first row sits right under the sticky
          // header, which would draw over an upward bubble.
          side="bottom"
          align="end"
          className="shrink-0"
        >
          <span
            data-testid="person-row-chip"
            className="inline-flex items-center whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-micro text-text-secondary"
          >
            {notAProfile ? "not a profile link" : "no LinkedIn"}
          </span>
        </Tooltip>
      </span>
    );
  }
  return (
    <button
      type="button"
      aria-label={rowEntryLabel(row)}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-testid="person-row-button"
      // The panel returns focus here on close, found by handle so it survives
      // the rows re-rendering.
      data-person-avatar={handle}
      data-person-row={row.import_contact_id}
      onClick={() => onOpen(row, handle)}
      className={`${ROW_ENTRY_LAYOUT_CLASS} rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
    >
      {avatar}
      {children}
    </button>
  );
}

/**
 * The LinkedIn URL as a small icon at the Name cell's right edge. A SIBLING
 * of the row button, never inside it, so following the link cannot open the
 * panel and needs no `stopPropagation`. Nothing when the row has no URL.
 *
 * NO `Tooltip` around it (iter-1 review of PR #970): the trigger span is a
 * tab stop of its own, so the icon cost two stops per row and announced the
 * same destination twice; and inside this `sticky z-10` cell the bubble
 * would paint UNDER the next row's frozen cells (same `z-10`, later in DOM).
 * The `<a>` is the surface: `aria-label` names it, `title` shows on hover.
 *
 * "profile" only when the URL is a `/in/` profile; a Sales Navigator or
 * `/pub/` URL says "link", in the words of the chip beside it.
 */
function LinkedInProfileLink({ row }: { row: ImportContactRow }) {
  const url = linkedinUrlOf(row);
  if (url === null) return null;
  // Protocol-optional: Combine & Filter's merged rows can arrive without a
  // scheme; a detail-page row already carries one and passes unchanged.
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  const kind = linkedinHandle(url) !== null ? "profile" : "link";
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="linkedin-link"
      aria-label={`Open ${fullName(row)}'s LinkedIn ${kind}`}
      title={`Open LinkedIn ${kind}`}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-accent-ink hover:bg-accent-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Linkedin aria-hidden="true" className="h-4 w-4" />
    </a>
  );
}

export function ContactsTable({
  contacts,
  columns,
  // INERT DEFAULTS, not decoration: `variant="preview"` renders neither the
  // filter bar nor the selection column, so the preview call site supplies
  // none of these. They still have to hold a value — `selectedIds.has(...)`
  // and the chip-count derivations run before the render branches — so the
  // defaults are the "nothing is filtered, nothing is selected" identity.
  nameQuery = "",
  onNameQueryChange = noop,
  reviewStatusFilter = "all",
  onReviewStatusFilterChange = noop,
  dupLinkedinFilter = false,
  onDupLinkedinFilterChange = noop,
  thriveIdFilter = "any",
  onThriveIdFilterChange = undefined,
  addToSearchFilter = "any",
  onAddToSearchFilterChange = undefined,
  skippedFilter = null,
  onSkippedFilterChange = undefined,
  onUndoLeaveOut = undefined,
  onUndoDifferentPerson = undefined,
  enrichmentFilter = "all",
  onEnrichmentFilterChange = noop,
  currentPositionFilter = "",
  onCurrentPositionFilterChange = noop,
  currentCompanyFilter = "",
  onCurrentCompanyFilterChange = noop,
  bctCompanyFilter = "",
  onBctCompanyFilterChange = noop,
  bctPositionFilter = "",
  onBctPositionFilterChange = noop,
  bctYearFrom = "",
  onBctYearFromChange = noop,
  bctYearTo = "",
  onBctYearToChange = noop,
  counts,
  selectedIds = EMPTY_SELECTION,
  onToggleSelect = noop,
  onToggleSelectAll = noop,
  selectionMode = "explicit",
  totalFilteredContacts = undefined,
  onSelectAllFiltered = noop,
  onClearCrossPageSelection = noop,
  sort,
  onSort,
  rationaleFor,
  notesQuery = "",
  onNotesQueryChange,
  notesMatchCount = null,
  notesTerms = NO_NOTES_TERMS,
  resolvedAvatars,
  isBct,
  importId,
  rowsArePlaceholder = false,
  writeOutcomeLabel = "Added to search",
  compareHasRun,
  variant = "detail",
  emailFilter = "",
  onEmailFilterChange,
  onOpenPerson,
  openPersonHandle = null,
  openPersonRowId = null,
  showSelectionColumn = false,
  dimUnselectedRows = false,
  richPreview = false,
  sourceListsFor,
  previewStageColumn = false,
  thriveColumns,
  stageFilter = null,
  onStageFilterChange,
  thriveAvatars,
}: {
  contacts: ImportContactRow[];
  columns: AnnexColumn[];
  /** Current name-search query. The parent filters + paginates SERVER-SIDE
   *  from this value; here it only drives the search input + the in-name
   *  match highlight (the `contacts` prop already arrives filtered). */
  /** Controlled chip filters — the parent forwards them as query params, so
   *  they narrow the WHOLE import (not just the visible page). */
  /** Controlled "Current Position Filters" — server-side over the contact's
   *  own promoted position. Rendered for EVERY format (no `isBct` gate),
   *  before the BCT section on the same line. */
  /** Controlled BCT Filters (rendered only when `isBct`) — server-side, over
   *  the displayed match reason. Years ride as free strings (input state);
   *  the parent parses them before building query params. */
  /** Whole-import chip buckets from the contacts response (under the other
   *  active filters). Absent while the first page is loading. */
  counts?: ImportContactsCounts;
  /** Active annex sort (by criterion `key`), or null/undefined for the
   *  default order. When `onSort` is provided the annex headers become
   *  clickable sort toggles. */
  sort?: ContactsTableSort | null;
  onSort?: (key: string) => void;
  /** Looks up the AI Review rationale for a given contact × criterion cell,
   *  rendered inline (scrollable) beneath the score. Absent for imports that
   *  were never AI-reviewed. */
  rationaleFor?: (contactId: string, criterionKey: string) => string | undefined;
  /** The "Keywords in AI notes…" box (Trello #779). Rendered beside the name
   *  search only when `onNotesQueryChange` is given — the page passes it on
   *  AI-reviewed lists alone. */
  notesQuery?: string;
  onNotesQueryChange?: (value: string) => void;
  /** The count line under the box; `null` hides it. */
  notesMatchCount?: { count: number; of: number | null; terms: readonly string[] } | null;
  /** Terms the server matched, highlighted inside each rationale. */
  notesTerms?: readonly string[];
  /** `profile_image_s3_key` → presigned S3 GET URL for contacts whose avatar we
   *  have stored (from `useResolveAvatars`). A row renders the durable S3 copy
   *  when its stored key resolves here; otherwise (no key, or presign still in
   *  flight) it shows initials. The licdn `profile_image_url` is never used. */
  resolvedAvatars?: ReadonlyMap<string, string>;
  /** When true, renders BCT match-reason columns (Matched Position, Matched
   *  Company, Years) and BCT-specific filter controls.
   *
   *  Keyed on `import_format` — a SCHEMA question: does this list carry BCT
   *  match columns? Deliberately NOT the same axis as `writeOutcomeLabel`
   *  below, and four tests in `ImportDetailPage.test.tsx` exist to stop the two
   *  being re-merged. See that prop for why. */
  isBct?: boolean;
  /** The import being shown. Scopes the width measurement, and nothing else.
   *
   *  Without it the SECOND import of a session inherits the first one's
   *  measured widths whenever their criteria keys match — which
   *  `useColumnWidths` says is the normal case, since the AI-Review criteria
   *  are "stable and reused" across imports. Four things conspire: this
   *  `importId` comes from `useParams` so React Router reconciles rather than
   *  remounts, both contacts queries use `placeholderData: keepPreviousData` so
   *  `contacts` never goes undefined during the switch, and there is no loading
   *  gate in the render.
   *
   *  CORRECTION: this paragraph used to open "the component takes no `key` at
   *  its render site". It does — `key={importId}`, added by #685, long before
   *  this card — so the latch cannot in fact survive an import→import
   *  navigation, and the story below describes a hazard the page had already
   *  closed. The prop is kept as component-level defence, which the unit tests
   *  (rendered with no key) genuinely exercise; what was wrong was the lesson,
   *  not the code (`/pr-review` round 11, Agent A MINOR 10).
   *  Found by `/pr-review` Agent A, who demonstrated it rather than argued it:
   *  import A measured at 160px, import B with the same keys rendered 160px and
   *  never measured. The symptom is order-dependent — open the terse list first
   *  and the verbose one opens too narrow; open them the other way round and it
   *  is correct — which is the nastiest shape a layout bug can have. */
  /** Optional: the AI Review preview renders before an import is necessarily
   *  selected, and an absent id simply leaves the measurement signature
   *  unscoped — the same state the unit tests render in. */
  /** True while `contacts` is the PREVIOUS import's rows, held by
   *  `placeholderData: keepPreviousData` during a navigation.
   *
   *  REQUIRED, and it is the one prop here with no safe default. `false` means
   *  "these rows are real, measure them", so a forgetful caller silently gets
   *  the latch bug back; `true` cannot be the default either, because this is a
   *  skip rather than a key and `true` would disable measurement permanently.
   *  With no safe default the only protection is making the caller state it
   *  (`/pr-review` Agent B). `importId` above is required for the same family
   *  of reason: one render site today, so it costs nothing, and a second one
   *  omitting either prop would revert a fix that took a review round to find,
   *  with no test able to see it.
   *
   *  This is a skip, never a key — see the effect. It exists because the
   *  signature is assembled from sources that do not update together:
   *  `importId` (URL) and `rationaleFor` (its own query, which has NO
   *  placeholder and whose key includes the import) both flip immediately,
   *  while `columns`/`contacts` lag behind a placeholder. Between those two
   *  moments the signature names import B over import A's DOM, and a
   *  measurement taken there is of a state that was never really on screen —
   *  yet it claims B's final signature, so B never measures again. */
  /** Optional: the AI Review preview has no placeholder hop — its rows arrive
   *  once, from a query with no `keepPreviousData` — so it never needs to
   *  suppress a measurement, and `false` is the honest default. */
  /** What the write-outcome column is CALLED — "Added to search" on a candidate
   *  list, "Added to Thrive" on a Company Map.
   *
   *  Supplied by the page rather than derived here, for the same reason
   *  `ExportToThrivePanel` takes `actionLabel`: the page owns `addAction`, the
   *  one object that decides what this write is called and which route it
   *  takes, and a second derivation here would be a copy to keep in sync.
   *
   *  It is also a different AXIS from `isBct`. That one is `import_format`
   *  (schema); this follows `source`, because what the column reports is what
   *  the export worker DID, and the worker reads `source`. The uploadable
   *  literals let the two disagree — `source="bct", import_format="baby_chops_v1"`
   *  is legal — so keying the header on the format would name the wrong
   *  destination for exactly the row the split exists to get right.
   *
   *  Past tense on purpose: the cell shows an OUTCOME (a check, blank,
   *  "Skipped", "Failed"), not a button. */
  writeOutcomeLabel?: string;
  /** Whether Compare to Thrive has EVER run for this import.
   *
   *  Drives the Thrive ID cell's empty state: "Not found" is only true once a
   *  lookup happened. Before that the honest rendering is a dash, because
   *  nobody has looked. `conflict_count === 0` cannot answer this — it reads 0
   *  for never-run AND for ran-with-no-conflicts — which is why the API grew
   *  `compared_at` (cl.4 item 13). */
  /** Which surface is rendering this table.
   *
   *  `"detail"` (the default) is the Import Detail page: the full table, with
   *  selection, the filter bar and the export/Thrive/enrichment columns.
   *
   *  `"preview"` is the AI Review preview, BEFORE a run is applied. It shows
   *  identity plus scores and nothing else, because nothing else has happened
   *  yet: the rows are not exported, not compared, not enriched and not
   *  selectable, so the four columns reporting those outcomes would render a
   *  dash in every cell, and a filter bar would narrow a five-row sample.
   *
   *  This is ONE prop rather than a bag of `showX` booleans on purpose. The
   *  card behind it (Trello #729) asks for "the same view" on both surfaces,
   *  and independent toggles are how two surfaces drift apart again — a named
   *  variant makes the difference between them reviewable in one place. It
   *  follows `isBct` above, the prop that already gates whole columns.
   *
   *  So the preview renders: Name · the annex criterion columns · Total — and,
   *  for a Company Map import, the three `isBct` columns too (Matched Position /
   *  Matched Company / Years). Those sit OUTSIDE the `!isPreview` guards on
   *  purpose: they are match-REASON context, not the outcome of a run, so they
   *  answer "why is this row here at all?" on the preview exactly as they do on
   *  the detail page — which is the parity the card asks for. `AiReviewPage`
   *  passes `isBct` for `import_format === "bct"` while the run is unapplied, so
   *  a Company Map preview is three columns wider than a CSV one. (The BCT
   *  FILTER bar is a different matter and is nested inside `!isPreview`, so it
   *  stays hidden — a five-row sample has nothing to filter.)
   *
   *  One more difference, listed here because this docstring claims to be the
   *  one place they are reviewable: the preview's headers do not SORT. The
   *  preview arm passes no `onSort`, so `sortable` is false and every `<th>`
   *  renders `col.label` as plain text where the detail page renders a button
   *  with a `↕`. Deliberate — sorting is server-side over the whole import and
   *  a five-row sample has no page to re-order — but it does mean the header
   *  row is NOT byte-identical across the two surfaces, and it flips the
   *  `sortable` entry of the measurement signature (harmless: that signature is
   *  a per-mount ref, so the surfaces never shared a measurement anyway).
   *
   *  What it deliberately does NOT change: the Name cell, the annex criterion
   *  columns, the Total, the score badges and the in-cell rationale. Those are
   *  the parts the card is about, and they must stay byte-identical across the
   *  two surfaces. (There is no Connection column to keep — cl.4 item 7 removed
   *  it from this table; `connection_degree` now rides in the Name cell.) */
  variant?: "detail" | "preview";
  /** Controlled email filter — forwarded as `q_email` to the server. */
  emailFilter?: string;
  onEmailFilterChange?: (value: string) => void;
  /** Person side panel entry (flag `person-panel`). When set, the Name cell
   *  of every row with a LinkedIn profile (avatar, name and position line)
   *  becomes ONE button that opens the panel for that row, and the whole row
   *  hovers; absent (flag off, or the preview), the cell is plain. The
   *  LinkedIn icon at the cell's right edge links out either way. */
  onOpenPerson?: (row: ImportContactRow, handle: string) => void;
  /** The handle the panel is open on — that row is highlighted. */
  openPersonHandle?: string | null;
  /** The ROW the panel acts on, when the page has it. A profile listed twice
   *  highlights the row that was clicked, not both. */
  openPersonRowId?: string | null;
  /**
   * PREVIEW-ONLY, additive (Combine & Filter round 2, sub-task R2-B): shows
   * the selection checkbox column even though `variant="preview"`. Every
   * OTHER preview caller (AI Review's `ScoresTable`) omits this and keeps
   * rendering with no checkbox column, byte-identical to before this prop
   * existed — an explicit opt-in rather than inferred from which selection
   * callbacks were passed, since this component already defaults every one
   * of those to a no-op for the preview arm's convenience (see the "INERT
   * DEFAULTS" comment on the destructure below), which erases the signal a
   * presence check would need. Ignored on `variant="detail"` (always shown
   * there). */
  showSelectionColumn?: boolean;
  /**
   * OPT-IN: a row that is not in `selectedIds` renders dimmed with its name
   * struck through. Combine & Filter's Review table, where unticking a contact
   * means "leave this person out", sets it so an excluded row still reads as
   * excluded (lost when the table moved here in 65d9838fd). Every other caller
   * leaves it `false`: on the detail page an unselected row is the NORMAL state
   * and must not look removed. The dim skips the sticky (frozen) cells, whose
   * opaque surface would turn translucent under `opacity` and show scrolled
   * content through it; the name carries its own dim instead.
   */
  dimUnselectedRows?: boolean;
  /**
   * PREVIEW-ONLY, additive (round 2, sub-task R2-B): renders the Thrive ID,
   * Location and Lists columns on the preview variant — the set
   * Combine & Filter's Review-contacts table needs and AI Review's preview
   * does not. `false` (every other preview caller) renders none of them,
   * unchanged from before this prop existed. Ignored on `variant="detail"`.
   */
  richPreview?: boolean;
  /** PREVIEW-ONLY (`richPreview`): the people lists a row came from, for the
   *  Lists column — `id` keys the pill (two lists can share a title), `title`
   *  is what it reads. `undefined` renders a dash. */
  sourceListsFor?: (row: ImportContactRow) => readonly { id: string; title: string }[] | undefined;
  /** PREVIEW-ONLY (`richPreview`): a Stage column after Lists, for a Combine
   *  & Filter result that carries Thrive candidacy stages (05). */
  previewStageColumn?: boolean;
  /** A list pulled from a Thrive search (04): appends Stage · In stage ·
   *  Added after Contact info and the stage chips to the first filter row.
   *  Absent on every other list, which renders exactly as before. */
  thriveColumns?: ThriveColumnsContext;
  /** The stage chip in force (one at a time), or null. Server-side. */
  stageFilter?: string | null;
  onStageFilterChange?: (stage: string | null) => void;
  /** Thrive contact id → photo URL (`useResolveThriveAvatars`), for a
   *  Thrive-sourced list: its rows resolve by `thrive_source_contact_id`
   *  rather than by a stored LinkedIn avatar key. */
  thriveAvatars?: ReadonlyMap<string, string>;
} & ContactsTableSurfaceProps) {
  // Drag-to-resize for the dynamic annex (AI-Review) columns. Persisted widths
  // live in `useColumnWidths` (localStorage); the render reads only the
  // committed width. During the live gesture we write the dragged `<th>`'s width
  // IMPERATIVELY (rAF-coalesced) instead of via React state, so a pointermove
  // does NOT re-render the whole <tbody> once per frame (would scale with row
  // count). React state is touched exactly once, on pointerup / keydown.
  //
  // TWO WRITERS, ONE `<th>`: the inline `style`/`aria-valuenow` set by React on
  // render, and the imperative `thEl.style.*` writes here. React diffs those
  // props per-property against its OWN previous props (never the live DOM), so a
  // commit re-render only touches the DOM when the committed value DIFFERS from
  // the last value React rendered. That means the commit path (`endResize`) must
  // write the final width to the DOM itself — it cannot rely on the re-render to
  // resync, because when the gesture ends back on the pre-drag width React writes
  // nothing and the last imperative frame would otherwise stick forever.
  // `"contacts"` is the table namespace in the persisted width map — see
  // `useColumnWidths`, which used to key by the bare column key and so could
  // only ever serve one table.
  const { widthFor, setWidth, resetWidth } = useColumnWidths("contacts");
  const resizeOrigin = useRef<{
    key: string;
    isFill: boolean;
    /** The fill column's floor when the gesture began — the value React was
     *  rendering into `min-width`, which is what the gesture actually moves.
     *  Distinct from `startWidth`, which for a fill column is the RENDERED
     *  width so the drag can be relative to the cursor. */
    floorAtStart: number;
    pointerId: number;
    startX: number;
    startWidth: number;
    // Whether React renders an EXPLICIT width for this column — an override of
    // its own, or a call-site fallback (the annex columns were built pinned and
    // always supply `DEFAULT_ANNEX_WIDTH`). Not the same as "has an override":
    // an unresized annex column has no override and is still React-pinned.
    //
    // The two-writers note above only covers half the trap. When React renders
    // `style={undefined}` it keeps rendering `undefined`, so an imperative write
    // is never diffed away, and `resetWidth` (double-click) removes a state key
    // that was never set — also a no-op. A gesture on such a column that ends
    // without committing must therefore CLEAR the DOM itself. See `endResize`.
    reactPinsWidth: boolean;
    thEl: HTMLTableCellElement;
  } | null>(null);
  const resizeRaf = useRef<number | null>(null);
  const pendingWidth = useRef<number | null>(null);

  /**
   * THE IMPERATIVE WRITERS DO NOT TOUCH THE FILL COLUMN. That is the whole fix,
   * and it is smaller than the three that preceded it.
   *
   * Rounds 8, 9 and 10 each found the same defect in a new costume: `fillStyle`
   * failing to emit `minWidth`, then `applyThWidth` stranding `width` and
   * `maxWidth`, then `clearThWidth` stripping `minWidth`. Every fix taught one
   * more writer one more property of a three-property shape, and the next
   * unvisited cell of that matrix produced the next blocker.
   *
   * The bisection was there the whole time and nobody read it as one: THE
   * KEYBOARD PATH WAS CORRECT IN EVERY ROUND, because `keyResize` only calls
   * `setWidth` and never writes to the DOM. So the bug was never "which
   * properties does a writer get wrong" — it was that a writer touches this
   * column at all.
   *
   * These functions exist to dodge a re-render of `<tbody>` per animation frame
   * during a drag, which matters because that cost scales with row count. The
   * fill column is ONE column, and there is nothing to RESTORE for it — React
   * renders `fillStyle` unconditionally, so the DOM is never out of step.
   *
   * THE PREVIEW IS FORFEITED, DELIBERATELY, AND IT IS NOT FREE. An earlier
   * version of this paragraph said "there is nothing to preview", which is true
   * of most drags and false of the ones that matter. Dragging this grip moves a
   * floor, and while the floor stays below the fill width nothing on screen can
   * change — but raising it PAST that width is the one thing the grip can
   * visibly do, and that is exactly where the missing preview bites: measured,
   * a 200px drag on a column filling at 876 shows nothing for the whole gesture
   * and then moves the full 200px in one step on release. The column ends
   * exactly where the pointer put it; it just arrives late. An earlier revision
   * of this paragraph said "jumps 756px", which was the FLOOR's movement
   * (320 -> 1076) and not anything anyone sees — the reviewer who measured it
   * caught his own number a round later (`/pr-review` rounds 11-12, Agent C).
   * The true statement is the more useful one: the gesture is accurate and
   * unresponsive, which is what you need to know before re-adding the writer.
   * A scary number sends the next reader hunting an overshoot that is not
   * there, failing to find it, and concluding the paragraph is alarmist.
   * The narrowing direction is invisible on the page you perform it on.
   *
   * The trade is made knowingly: a writer that touches this column AT ALL
   * produced a blocker in three consecutive rounds, and a fourth rendering
   * property would produce another. Losing one column's live preview buys the
   * whole class. Anyone tempted to re-add the writer should know it must get
   * three properties right at all EIGHT call sites — `flushResize` (1),
   * `beginResize`'s outgoing branch (2), `endResize` (3), `cancelResize` (2) —
   * and that is what the three rounds were.
   *
   * Named by FUNCTION, not by line. This sentence first said "six", which was
   * the same undercount the `widthStyle` docstring below already had to correct
   * once; the fix cited eight line numbers, and by the time it was committed
   * all eight were wrong — the comment blocks added in that very commit had
   * pushed every one of them down by 17 or 27 lines (`/pr-review` round 13,
   * Agent A NIT 13). A sentence that corrects an undercount by enumerating what
   * it did not re-check is the same mistake wearing the fix's clothes. Function
   * names survive edits, and they tell you WHERE IN THE GESTURE each call sits,
   * which is the thing a reader actually needs.
   *
   * One consequence to name rather than discover: `aria-valuenow` is stale for
   * the duration of a fill-column drag, for the same reason. The paragraph
   * below explains why that matters everywhere else.
   */
  const applyThWidth = (thEl: HTMLTableCellElement, px: number, isFill: boolean) => {
    if (isFill) return;
    const w = `${px}px`;
    thEl.style.width = w;
    thEl.style.minWidth = w;
    thEl.style.maxWidth = w;
    // Keep the separator's ARIA value in lockstep with the live DOM width. The
    // gesture bypasses React state, so `aria-valuenow` would otherwise report the
    // pre-drag width to assistive tech for the whole drag (and, after a
    // return-to-start drag, permanently — same no-op-diff trap as the width).
    // Select by the ARIA role (there is exactly one separator per `<th>`), not a
    // `data-testid`, so this survives any future test-id stripping in the build.
    thEl.querySelector('[role="separator"]')?.setAttribute("aria-valuenow", String(px));
  };
  /** Hand a column back to the browser.
   *
   *  The `isFill` guard is defence on a path THE SUITE CANNOT REACH — which is
   *  not the same as unreachable, and an earlier version of this note inferred
   *  the second from the first.
   *
   *  Every fill column carries a fallback (`CONTACT_INFO_WIDTH`,
   *  `pinnedFallback`, `MIN_WIDTH`), so `reactPinsWidth` is true for one and
   *  the callers take `applyThWidth` instead. Deleting this line leaves the
   *  suite green — but green-under-mutation shows the TESTS cannot reach it,
   *  not that nothing can.
   *
   *  What actually gates it is a property of the DATA: `isFillColumn` is a
   *  string comparison, and annex keys are free text (`build_annex_columns`
   *  sets `key = criterion_name`). A criterion literally named `name`, `export`
   *  or `thrive_id` that happens to be last makes the STATIC column of that
   *  name report `isFill: true` while its own handle passes no fallback, which
   *  is both conditions this branch needs. A reviewer reached it that way with
   *  a live probe (`/pr-review` round 11, Agent C).
   *
   *  Those three and no others: `lastColumnKey` is `isBct ? "bct_years" : ...`,
   *  so on a Company Map list the fill key is hardcoded and no criterion can
   *  take it, and on a non-BCT list the `bct_*` `<th>`s are not rendered at all.
   *  An earlier revision listed `bct_position` here (round 12, Agent C).
   *
   *  Harmless in THIS branch — nothing imperative wrote to the column, so the
   *  early return and the removal it replaces are both no-ops. Not harmless in
   *  general, and that changed in round 12: `beginResize` now takes `isFill`
   *  from the render site precisely so the collision cannot hand a static
   *  column a fill column's floor. Before that it did, and a 40px narrowing
   *  drag from 300 committed 128. */
  const clearThWidth = (thEl: HTMLTableCellElement, isFill: boolean) => {
    if (isFill) return;
    thEl.style.removeProperty("width");
    thEl.style.removeProperty("min-width");
    thEl.style.removeProperty("max-width");
    thEl.querySelector('[role="separator"]')?.removeAttribute("aria-valuenow");
  };
  const flushResize = () => {
    resizeRaf.current = null;
    const origin = resizeOrigin.current;
    if (origin && pendingWidth.current != null) {
      applyThWidth(origin.thEl, pendingWidth.current, origin.isFill);
    }
  };
  const beginResize =
    (key: string, fallback: number | undefined, isFillColumn: boolean) =>
    (e: React.PointerEvent<HTMLSpanElement>) => {
      // Stop the pointerdown from reaching the sort button / starting a text
      // selection; capture so moves keep flowing when the cursor leaves the strip.
      e.preventDefault();
      e.stopPropagation();
      const thEl = e.currentTarget.closest("th");
      if (!thEl) return;
      // Clear any frame/pending width left over from a gesture that ended
      // abnormally (pointer capture lost with no pointerup/cancel). Otherwise a
      // stale queued `flushResize` would fire against THIS newly-grabbed `<th>`
      // and snap it to the previous drag's width.
      stopRaf();
      pendingWidth.current = null;
      // `isFillColumn` is the CALLER's, deliberately, and this used to be
      // `key === lastColumnKey` re-derived here. Two comparisons of the same
      // pair, written out in two places, can disagree — and they do: annex keys
      // are unvalidated free text (`key = criterion_name`), so a criterion named
      // `export` renders a SECOND `<th>` with `data-key="export"` and the STATIC
      // export column then answers true here while React renders it with
      // `widthStyle`, not `fillStyle`. That column is not a fill column by any
      // other measure, and it inherited a fill column's floor: `floorAtStart`
      // becomes MIN_WIDTH, so the round-12 narrowing clamp commits 128 on a 40px
      // drag from 300 and persists it for every import. Taking the flag from the
      // same expression that chose the style makes the two identical BY
      // CONSTRUCTION rather than by inspection — the discipline this file
      // already applies to `widthStyle`/`reactPinsWidth`. `/pr-review` round 12,
      // Agents A (MINOR 12) and C, who reached it from opposite directions.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      // A second `pointerdown` on ANOTHER handle while this gesture is live used
      // to overwrite `resizeOrigin.current` outright. The first pointer's
      // `pointerup` then hits the `origin.pointerId !== e.pointerId` guard and
      // returns, so a static unresized column was left holding an imperative pin
      // with no committing writer and React rendering `style={undefined}` for
      // ever — the exact state the three-branch commit above exists to prevent,
      // reached by a different route. Reachable on a touchscreen: `touch-none`
      // stops scrolling, not a second finger. `/pr-review` iter 7 (Agent A A4).
      const outgoing = resizeOrigin.current;
      if (outgoing && outgoing.key !== key) {
        // `outgoing.isFill`, NOT a literal `false`. This branch opted itself out
        // of the fix that was supposed to end the whole class: every fill column
        // carries a fallback, so `reactPinsWidth` is always true for one, which
        // means the fill column ALWAYS took the path that had been told it was
        // not a fill column. A second pointer landing mid-drag stranded it at
        // `1334 / 1334 / 1334` with `width:"100%"` and `maxWidth:"none"` gone
        // for good — fifth occurrence of one defect, and the only one that was
        // a typo rather than a design gap (`/pr-review` round 11, Agent A).
        if (outgoing.reactPinsWidth) {
          applyThWidth(outgoing.thEl, outgoing.startWidth, outgoing.isFill);
        } else {
          clearThWidth(outgoing.thEl, outgoing.isFill);
        }
      }
      resizeOrigin.current = {
        key,
        pointerId: e.pointerId,
        startX: e.clientX,
        // No override yet (every static column, until someone drags it) means the
        // browser owns the width, so the gesture has to start from what the
        // column MEASURES rather than from a number we invented. Rounded, because
        // `getBoundingClientRect` returns sub-pixel values and every downstream
        // width is an integer px.
        // A POINTER GESTURE ON A FILL COLUMN ANCHORS TO WHAT IS ON SCREEN.
        // `widthFor ?? fallback` is the FLOOR, and for a fill column the floor
        // is nowhere near the rendered width — measured, `contact_info` renders
        // 1334 while its fallback is 320. Anchoring there made the drag
        // arithmetic relative to a number the user cannot see: a 400px LEFT
        // drag committed `clamp(320-400)` = 128, persisting a floor BELOW the
        // 320 pin globally for every import — the shredded-address defect this
        // commit's own floor exists to prevent — and a 300px RIGHT drag
        // committed 620, i.e. NARROWED a column it was widening, with
        // `movesWrongWay` blind to it because 320 -> 620 is honestly wider
        // (`/pr-review` round 10, Agents B §5 and C MAJOR-1).
        //
        // The keyboard deliberately does NOT follow: `keyResize` keeps reading
        // the floor, because a 16px step has no cursor to be relative to and
        // anchoring IT to the rendered width is what made one ArrowLeft raise
        // the floor by 732px. Two gestures, two anchors, because they mean
        // different things — the pointer moves an edge the user is holding, the
        // keyboard nudges a value.
        startWidth: isFillColumn
          ? Math.round(thEl.getBoundingClientRect().width)
          : (widthFor(key) ?? fallback ?? Math.round(thEl.getBoundingClientRect().width)),
        reactPinsWidth: widthFor(key) != null || fallback != null,
        // The fill column renders a shape neither imperative writer can
        // reproduce, so both of them need to know. NOT derivable from
        // `reactPinsWidth`: un-dragged `bct_years` has that false while
        // `fillStyle` did emit a style — the exact divergence probe C caught.
        isFill: isFillColumn,
        floorAtStart: widthFor(key) ?? fallback ?? MIN_WIDTH,
        thEl,
      };
      // AFTER the new origin is assigned, deliberately. Belt-and-braces for the
      // abnormal end this function already recovers from: a gesture that died
      // with no pointerup/cancel still owes a measurement, and without this a
      // lost pointer capture would leave measurement disabled until something
      // else moved a dep.
      //
      // It does NOT measure mid-gesture, and it does not itself recover
      // anything — an earlier version of this comment claimed it did. The token
      // bump re-runs the effect, which finds `resizeOrigin.current` set to THIS
      // gesture, re-arms the flag and skips, so the debt is simply deferred to
      // the new gesture's end. `endResize` is what pays it, and `endResize`
      // alone would have covered the abnormal-end case too. This call is cheap
      // insurance, not the mechanism (`/pr-review` Agent A).
      //
      // If the user never touches a handle again, neither call site helps —
      // what covers THAT is `resetAndRefit`, because a double-click is itself
      // two full pointerdown/up pairs, so `endResize` clears the stale origin
      // before the reset runs.
      //
      // Ordered after the assignment rather than before it: both orders behave
      // identically, but only because React batches the state update until the
      // handler returns. After makes it true by construction instead of by
      // scheduling (`/pr-review` Agent C, N1).
      flushSkippedMeasurement();
    };
  const moveResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    const origin = resizeOrigin.current;
    if (!origin || origin.pointerId !== e.pointerId) return;
    const delta = e.clientX - origin.startX;
    // BOTH guards the commit uses, not one. An earlier revision asked the rule
    // here and not the tolerance, while claiming "same rule as the commit" — so
    // a 1px jitter on a measured-1050 column committed nothing but still PAINTED
    // `clampWidth(1049)` = 640, snapping the column 410px narrower and back.
    // That is the "UI fighting the user" this guard exists to prevent, arriving
    // through the other half of the pair (`/pr-review` iter 9, Agent C C13).
    //
    // The preview is computed FIRST because the tolerance now depends on what
    // the gesture would DO, not merely on how far the pointer went. `clampWidth`
    // is pure, so the reorder is free. Preview and commit must use the same
    // threshold, or a 4px twitch paints a snap it will then decline to keep —
    // which is C13 again, through the other half of the pair.
    const preview = clampWidth(origin.startWidth + delta);
    if (Math.abs(delta) < toleranceFor(origin.startWidth, preview, delta)) return;
    // `delta` is non-zero here — the tolerance test above guarantees it — so
    // the sign is a total function into the intent.
    if (movesWrongWay(origin.startWidth, preview, delta < 0 ? "narrower" : "wider")) return;
    pendingWidth.current = preview;
    // Coalesce rapid pointermoves into one DOM write per frame.
    if (resizeRaf.current == null) {
      resizeRaf.current = requestAnimationFrame(flushResize);
    }
  };
  const stopRaf = () => {
    if (resizeRaf.current != null) {
      cancelAnimationFrame(resizeRaf.current);
      resizeRaf.current = null;
    }
  };
  // Cancel any queued frame on unmount (navigate away / filter change mid-drag)
  // so `flushResize` never fires against a detached `<th>`. Refs only — the
  // effect is mount-once and needs no deps.
  useEffect(
    () => () => {
      if (resizeRaf.current != null) cancelAnimationFrame(resizeRaf.current);
    },
    []
  );
  const endResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    const origin = resizeOrigin.current;
    if (!origin || origin.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    stopRaf();
    const raw = clampWidth(origin.startWidth + (e.clientX - origin.startX));
    // A NARROWING GESTURE ON A FILL COLUMN MUST NOT RAISE ITS FLOOR.
    //
    // The pointer anchors to the RENDERED width while the value it commits is
    // the FLOOR, and `movesWrongWay` cannot see the difference: it is handed
    // `from = 1334` (rendered) and `target = 934` (floor), correctly answers
    // "did not widen", and is blind to the fact that neither number is the one
    // that changed. So a leftward drag on a column filling at 1334 raised the
    // floor 320 -> 934 — silently, since nothing on screen moves, and persisted
    // globally for every import (`/pr-review` round 11, Agent A MAJOR 10).
    //
    // COUPLED, and only invisibly so: `moveResize` still computes its preview
    // as `clampWidth(startWidth + delta)` with no such clamp. That is a no-op
    // today for one reason only — `pendingWidth`'s single reader is
    // `flushResize`, which passes `origin.isFill` to `applyThWidth`, which
    // early-returns. Re-enable the imperative writer for fill columns, which
    // the docstring above anticipates, and `moveResize` paints a preview that
    // `endResize` then declines to keep: "a twitch paints a snap it will then
    // decline to keep", which this file already carries a rule against
    // (`/pr-review` round 12, Agent A).
    //
    // Clamping "narrower" to the existing floor makes the word mean what it
    // says: the gesture either lowers the floor or does nothing. Widening is
    // untouched and stays visible, because raising the floor past the fill
    // width is the one thing dragging this grip can visibly do.
    const final =
      origin.isFill && e.clientX < origin.startX ? Math.min(raw, origin.floorAtStart) : raw;
    // Commit on POINTER MOVEMENT, not on a width comparison. The width test this
    // replaces was wrong for exactly the columns checklist 7 added, because a
    // static column's `startWidth` is MEASURED and therefore unclamped while
    // `final` is clamped, so the two disagree for reasons the user did not cause:
    //   - measured ABOVE the old 640 ceiling (Name is the one fluid column and
    //     takes whatever the pinned ones leave): a no-move click gave
    //     `final === 640 !== startWidth`, so the column SNAPPED to 640,
    //     persisted globally for every import. THAT half is unreachable now —
    //     with no ceiling a measured 1050 clamps to itself — but the movement
    //     test stays, because the FLOOR still produces the same shape for a
    //     column measuring under 128. The keyboard had its own copy of this —
    //     `keyResize` never routes through here — and it is fixed separately,
    //     in that function. An earlier revision of THIS comment said "`End` on
    //     the keyboard did the same", past tense, while the keyboard bug was
    //     still live (`/pr-review` iter 7, Agent C C5).
    //   - measured within the clamps: `final === startWidth`, so nothing was
    //     persisted, but `applyThWidth` had already pinned the `<th>` inline and
    //     React — still rendering `style={undefined}` — could never diff it away.
    //     `resetWidth` removes a key that was never set, so double-click could
    //     not clear it either. The pin survived every interaction until reload.
    // Found by `/pr-review` iter 6 (Agent C, C1).
    const delta = e.clientX - origin.startX;
    // Two guards, not one. `movesWrongWay` is the shared rule, imported — the
    // pointer path never had it, so dragging Name RIGHT from a measured 1050
    // committed 640 and persisted it for every import (iter 8, Agent C C10).
    // `DRAG_TOLERANCE_PX` is what makes the no-move-click guard survive contact
    // with a real mouse.
    const intent: ResizeIntent = delta < 0 ? "narrower" : "wider";
    const moved =
      Math.abs(delta) >= toleranceFor(origin.startWidth, final, delta) &&
      !movesWrongWay(origin.startWidth, final, intent);
    if (moved && final !== origin.startWidth) {
      // Make the DOM authoritative BEFORE committing: when `final` equals the
      // last width React rendered, the commit re-render is a no-op diff and
      // would leave the last imperative frame's width on the `<th>` forever.
      applyThWidth(origin.thEl, final, origin.isFill);
      setWidth(origin.key, final, { from: origin.startWidth, intent });
    } else if (origin.reactPinsWidth) {
      // Nothing to commit, but React is rendering `startWidth` — resync the DOM
      // to it, because the last frame of a return-to-start drag is not it.
      //
      // NO LONGER ALWAYS THE LAST WORD. If this gesture also owes a
      // measurement, the flush below re-runs the pass, and for an
      // un-overridden annex column React then renders `pinnedFallback(new)`
      // against its own previous `pinnedFallback(old)` — different props, so it
      // writes over the value set here. That is correct (nothing was
      // committed, so the column should show the content fit, and the new fit
      // is the right answer) but it means an aborted drag CAN visibly resize
      // the column in the window where a signature input flipped mid-gesture.
      // Named because these three branches otherwise read as if the imperative
      // write settles it (`/pr-review` Agent A).
      applyThWidth(origin.thEl, origin.startWidth, origin.isFill);
    } else {
      // Never overridden and nothing committed: React renders `style={undefined}`
      // and always will, so the DOM has to be cleared here or the pin is
      // permanent.
      clearThWidth(origin.thEl, origin.isFill);
    }
    resizeOrigin.current = null;
    pendingWidth.current = null;
    flushSkippedMeasurement();
  };
  const cancelResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    const origin = resizeOrigin.current;
    if (!origin || origin.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    stopRaf();
    // Drag aborted → no commit, no re-render, so this is the only writer left.
    // Same split as `endResize`: restore the override React is rendering, or —
    // if there never was one — hand the column back to the browser rather than
    // leaving the aborted drag's last frame pinned on it forever.
    if (origin.reactPinsWidth) {
      applyThWidth(origin.thEl, origin.startWidth, origin.isFill);
    } else {
      clearThWidth(origin.thEl, origin.isFill);
    }
    resizeOrigin.current = null;
    pendingWidth.current = null;
    flushSkippedMeasurement();
  };
  const keyResize =
    (key: string, fallback?: number) => (e: React.KeyboardEvent<HTMLSpanElement>) => {
      // Same "measure when unresized" rule as the drag: a keyboard nudge on a
      // column nobody has touched has to step from its rendered width, not from
      // a default it never had.
      const current =
        widthFor(key) ??
        fallback ??
        Math.round(e.currentTarget.closest("th")?.getBoundingClientRect().width ?? MIN_WIDTH);
      // All THREE keys ask `movesWrongWay` — imported from `useColumnWidths`,
      // where the rule now lives and enforces itself. Only the FLOOR bites now
      // — `End` and the ceiling are both gone, so the upper half of the
      // original story is history rather than behaviour: a
      // widening key on a column MEASURING 1050 resolved to 640 and narrowed it
      // by 410px (iter 7), `ArrowLeft` on one measuring 70 WIDENED it to 128
      // (iter 8), and `Home` did the same from
      // its exemption (iter 9). What stays unguarded is the LANDING, not any
      // key: widening from 70 does land on 128, the narrowest legal override.
      // The old twin of that sentence — "narrowing from 1050 lands on 640" —
      // is false twice over now: it lands on 1034, and there is no widest
      // legal override at all.
      //
      // This comment has now outlived its code twice — "`ArrowLeft` is
      // deliberately NOT guarded" survived one iteration past its fix, and a
      // paragraph describing the pre-rule design survived stacked above its own
      // replacement. Both were caught by Agent B, in the diff, not after.
      //
      // `Home` used
      // to be exempt, on the argument that "as narrow as legal" is a request
      // rather than a direction — and below the floor that request WIDENS the
      // column: 70 -> 128, persisted globally. `End` carried the identical
      // argument and was guarded anyway, which is how the split showed up
      // (`/pr-review` iter 9 — Agent B B9-1, Agent A A1, Agent C C11/C12).
      // The component asks the rule too, and KEEPS asking it after the hook
      // started enforcing. Two reasons, both load-bearing:
      //   - `endResize` uses the answer to pick between three DOM branches; if
      //     only the hook refused, `applyThWidth` would still pin a `<th>` at a
      //     width React never renders. That is C1, resurrected.
      //   - here, so that a refusal INSIDE the hook is always a programming
      //     error. `ArrowLeft` on a below-floor column is a legitimate user
      //     action that legitimately does nothing; if it reached the hook it
      //     would warn on every keypress and the warning would stop meaning
      //     anything.
      // The hook's check is a backstop: the guarantee moves from "every caller
      // today opted in" to "no caller can opt out BY FORGETTING TO ASK". It is
      // not "no caller can write a wrong-way width": `from` is caller-supplied,
      // so a caller that declares a dishonest start still sails through. What
      // was bought is that the question can no longer be skipped.
      const commit = (target: number, intent: ResizeIntent) => {
        if (!movesWrongWay(current, target, intent)) {
          setWidth(key, target, { from: current, intent });
        }
      };
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        commit(clampWidth(current - STEP), "narrower");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        commit(clampWidth(current + STEP), "wider");
      } else if (e.key === "Home") {
        // `intent` is what the KEY MEANS, not what the arithmetic would do.
        // Deriving it as `MIN_WIDTH - current` was the first attempt and it is
        // wrong: on a column measuring 1050 that makes `End`'s intent NEGATIVE,
        // so the rule reads "widest" as a narrowing gesture and lets 640
        // through — re-opening iter 7's finding. A test caught it.
        e.preventDefault();
        commit(MIN_WIDTH, "narrower");
      }
      // NO `End`. Its whole meaning was "jump to the ceiling", and there is no
      // ceiling any more — `End` on an axis that is open above has no target to
      // name. `Home` survives because the FLOOR survives: below a cell's
      // min-content the pin stops being authoritative at all, so "as narrow as
      // legal" is still a place. The arrows are unaffected; they derive a
      // target rather than naming one.
    };
  /** Width style for a column, or nothing at all when nobody has resized it.
   *  `min == max == width` is what pins a column under `table-layout: auto`;
   *  omitting the style entirely is what leaves an untouched column fluid.
   *
   *  **KEEP IN LOCKSTEP with `reactPinsWidth` in `beginResize`.** That flag has
   *  to mean exactly "this function returned a style", and it recomputes the
   *  same `widthFor(key) ?? fallback` by hand. Verified equivalent at all
   *  **eight** width-bearing `<th>`s — six that pass no fallback (`name`,
   *  `export`, `thrive_id`, `bct_position`, `bct_company`, `bct_years`),
   *  `contact_info` with the constant `CONTACT_INFO_WIDTH`, and the annex map
   *  with `pinnedFallback(col.key, DEFAULT_ANNEX_WIDTH)` — a MEASURED value,
   *  no longer the bare constant. That last entry is the one that now needs
   *  care: a computed fallback can differ between two evaluations in a way a
   *  shared import cannot, so the annex `<th>` hoists it into `width` and
   *  passes THAT to both this function and its handle rather than calling
   *  `pinnedFallback` twice. An earlier revision said "six call sites", which
   *  counted neither the annex `<th>` nor its handle (`/pr-review` iter 9,
   *  Agent A N3); a later one still described the annex fallback as
   *  `DEFAULT_ANNEX_WIDTH` after it had stopped being that.
   *
   *  THREE OF THE EIGHT now conditionally route to `fillStyle` instead — the
   *  one that is `lastColumnKey`. `reactPinsWidth` stays honest through that
   *  because `fillStyle` always emits a style, so the flag's meaning ("this
   *  function returned a style") holds on either branch. Worth stating rather
   *  than leaving to be re-derived: it is now the thing keeping the flag true
   *  (`/pr-review` round 8, Agent C N6).
   *
   *  The agreement is hand-maintained and NO test would catch a divergence,
   *  because the tests exercise the columns as they are currently wired. A
   *  future `<th>` that passes a fallback here and forgets it on its handle (or
   *  the reverse) makes `reactPinsWidth` lie, and silently re-opens the bug
   *  where a gesture that commits nothing leaves an imperative pin React can
   *  never diff away. */
  const widthStyle = (key: string, fallback?: number) => {
    const px = widthFor(key) ?? fallback;
    return px == null ? undefined : { width: `${px}px`, minWidth: `${px}px`, maxWidth: `${px}px` };
  };

  /**
   * Content-derived defaults for the two families that are pinned from birth.
   *
   * `contact_info` and the annex columns are the only columns that pass a
   * fallback to `widthStyle`; the other six are fluid and the browser already
   * sizes them from content. Those two used to pass a CONSTANT, which is why a
   * criterion whose every rationale is "Yes" occupied the same 352px as one
   * holding two sentences. Now the constant is the ceiling and the measured
   * content width is the value.
   *
   * `useLayoutEffect`, not `useEffect`: the pass mutates the `<table>` to read
   * `max-content` off it and puts everything back before returning, and doing
   * that after paint would show a frame of the un-pinned table.
   *
   * ONCE PER SIGNATURE, and the signature is the whole design. The latch is
   * what stops the columns breathing as you page; the signature is what stops
   * the latch outliving the thing it measured. Both defects this round found
   * were the SAME missing invalidation, in two different inputs:
   *
   *   - `importId` — the component is never remounted across an import→import
   *     navigation, so import B inherited import A's widths whenever their
   *     criteria keys matched (which is the normal case). See the prop.
   *   - rationale AVAILABILITY — the annex cell renders a score badge plus a
   *     rationale, and the rationale arrives from a SEPARATE query
   *     (`useLatestAiReview`), racing the contacts query. Lose that race and
   *     the cells measure as badge-only, every criterion column latches at
   *     `MIN_WIDTH`, and the paragraphs then wrap inside 128px for the rest of
   *     the session — unrecoverable, because double-click falls back to the
   *     latched measurement. Presence, not content: `undefined -> defined`
   *     happens exactly once, so this re-measures once and never churns. An
   *     import whose review 404s never flips it and never re-measures, which is
   *     correct.
   *   - `remeasureToken` — bumped by double-click (`resetAndRefit`), by a
   *     released gesture and by a settled web-font load, see its note. Like
   *     every other input it is BOTH a dep and read here in the signature; a
   *     dep that is only listed and never read is what
   *     `useExhaustiveDependencies` objects to.
   *
   * The lesson, since I got it wrong twice: a latch is a cache, and a cache key
   * that omits an input the value depends on is the bug. Reasoning carefully
   * about which inputs are NOT deps (`isBct`, `widthFor` below) while missing
   * one that is, is exactly how this shipped.
   *
   * And the corollary, which is the part that predicts the NEXT occurrence
   * rather than recording the last: **this key is assembled from sources that
   * do not update together.** `importId` comes from the URL and
   * `rationaleFor` from a query with no placeholder — both flip at once —
   * while `columns` and `contacts` lag behind `keepPreviousData`. A key built
   * from families with different lag can name a state that was never on
   * screen. Every fix so far has been correct about the input it named and
   * incomplete about that mechanism; `rowsArePlaceholder` is the mechanism
   * being addressed directly, which is why it is a SKIP and not another key
   * component. A new input should be checked for its lag before it is added.
   *
   * ONCE PER COLUMN SET, not once per page. `contacts` is in the deps because
   * the first page has to arrive before there is anything to measure — but
   * re-measuring on every page would make the columns JUMP: page 1 of an
   * AI-reviewed import where every rationale is "Yes" fits at ~128px, page 2
   * where they are paragraphs re-fits to 352px, and paging back and forth
   * resizes the table under the reader. `measuredFor` pins the measurement to
   * the descriptor list, so a page change costs nothing and the widths a
   * recruiter learned on page 1 are the widths they get on page 9. The cost is
   * that a later page's longer rationales wrap inside a column sized for the
   * first page — which is what wrapping is for, and what the drag handle and
   * double-click are for.
   *
   * SKIPPED WHILE A GESTURE IS LIVE. The pass clears the pins to read
   * max-content, so a measurement taken mid-drag can move `naturalWidths` and
   * therefore what React renders for that `<th>` — after which `endResize`'s
   * no-commit branch writes the pre-refresh width against a post-refresh
   * render, and React's prev===next diff never corrects it. That is the
   * stranded imperative pin this file spent several rounds closing, reached by
   * a route the three-branch commit does not cover. `resizeOrigin.current` is
   * the whole guard.
   *
   * `isBct` is deliberately NOT a dep: the three Company Map columns it adds
   * carry no `data-col-key`, so they are not measured and cannot change what is.
   * Nor is `widthFor` — a resize commits an override, and an override outranks
   * the measurement anyway.
   *
   * Nor is `sort`, and that one is a JUDGEMENT rather than a non-input: it does
   * change the header, swapping a `↕` for a `▲`/`▼` of a different width — the
   * same argument that earned `onSort` its place two lines up. It is excluded
   * because the delta is a few px against a 128px floor, so the pin never stops
   * being authoritative, and because sorting also changes which rows are on the
   * page, which this latch deliberately ignores. Recorded rather than left out:
   * after `onSort` was added for exactly this reason, its sibling going
   * unmentioned would read as an oversight instead of a decision.
   *
   * Feedback-loop check: the effect calls `setNaturalWidths`, which re-renders,
   * but the deps below are untouched by that render so it does not re-run. The
   * setter also returns the previous object when nothing moved, so a re-measure
   * that agrees with itself costs no render at all.
   */
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [naturalWidths, setNaturalWidths] = useState<NaturalWidths>({});
  /** `!!onSort`, and the effect depends on THIS rather than on `onSort`.
   *
   *  THE `!!` IS NOT A STYLE CHOICE. TypeScript narrows through aliased
   *  conditions (4.4+), and `!!x` is a narrowing form while `Boolean(x)` is
   *  not — verified at tsc 5.9.3 on the real shape, optional prop plus a call
   *  inside an arrow callback: `Boolean` gives TS2722 at the call site,
   *  `!!` and `x !== undefined` are both clean. That single character is what
   *  lets the annex header branch on this same boolean and still call
   *  `onSort(col.key)` without an assertion.
   *
   *  What that buys. The header's branch and the measurement key are now the
   *  SAME reference, so the two-independent-expressions hazard — the one
   *  `widthStyle`'s docstring warns about, where either copy can be edited
   *  without the other — does not exist here at all.
   *
   *  WHAT THE COMPILER ENFORCES, and the rule is one-directional. It enforces
   *  SUFFICIENCY — this alias being true must imply `onSort` is defined,
   *  expressed in a narrowing form it recognises. It does not enforce
   *  NECESSITY: that `onSort` being defined implies `sortable`. Measured
   *  across seven aliases at tsc 5.9.3, on this file rather than a toy:
   *
   *    !!onSort                              CLEAN   (shipped)
   *    onSort !== undefined                  CLEAN
   *    !!onSort && columns.length > 0        CLEAN   <- and the suite passes
   *    columns.length > 0 && !!onSort        CLEAN   <- it too, all 163
   *    Boolean(onSort)                       TS2722  (form it does not recognise)
   *    !!onSort || columns.length > 0        TS2722  (sufficiency broken)
   *    !!rationaleFor                        TS2722  (wrong subject)
   *    columns.length > 0                    TS2722  (+ 2 suite failures)
   *
   *  So the one dangerous edit is NARROWING the alias: `&&` weakens necessity
   *  while preserving sufficiency, slips past both the compiler and the suite,
   *  and mis-keys the measurement — a cosmetic width, and this paragraph is
   *  its only guard. Weakening it, or changing its subject, is caught by the
   *  compiler; a wholly wrong subject is caught by the tests as well.
   *
   *  Worth knowing that the pass and the fail are both orthogonal to
   *  correctness: `Boolean(onSort)` is SEMANTICALLY IDENTICAL to what ships
   *  and is rejected, while `&& columns.length > 0` is semantically wrong and
   *  is accepted. The compiler is not reasoning about equivalence — it is
   *  asking whether this expression, in a shape it recognises, makes the call
   *  below safe.
   *
   *  This paragraph has now been written three times: once claiming the
   *  compiler catches nothing semantic, once claiming it catches everything,
   *  and once measured (`/pr-review` Agents A, B and C, each of whom tested a
   *  case the previous version had not).
   *  The parent passes `clientMode ? handleSort : undefined`, and `handleSort`
   *  is a plain function declaration — a new identity every render — so
   *  depending on the function itself made the dep array never equal and ran
   *  the effect body on every keystroke, poll and selection change. Harmless
   *  (the signature reads only the boolean, so it early-returned) but it
   *  falsified the feedback-loop paragraph below, which is the failure mode
   *  this card has spent four review rounds on. Depending on what the signature
   *  actually consumes restores the invariant instead of documenting around it.
   *  (`/pr-review` Agent A, MINOR-H.) */
  const sortable = !!onSort;
  const measuredFor = useRef<string | null>(null);
  /** Bumped to force one more measurement pass. Three bumpers, each a moment
   *  the latched measurement is known to be owed or stale: double-click, which
   *  is a user saying "fit this to what is on screen NOW" and gives every
   *  stale-latch scenario a user-reachable escape hatch, including whichever
   *  one nobody has thought of yet; `flushSkippedMeasurement`, for a pass
   *  skipped under a live gesture; and a web-font load that was in flight
   *  while a pass ran (`awaitingFonts` below). */
  const [remeasureToken, setRemeasureToken] = useState(0);
  /** True while a re-measure is owed to a font load in flight. One waiter at a
   *  time: `document.fonts.ready` is the same promise for every pass taken
   *  during one load, so a second `then` would only bump the token twice.
   *
   *  A REF AND NOT AN EFFECT CLEANUP, deliberately. The measuring effect
   *  re-runs on every poll (`contacts` is a dep) and returns early on an
   *  unchanged signature; a waiter cancelled by that cleanup would never be
   *  re-armed, and the measurement taken with the fallback's metrics would
   *  stay latched, which is the defect this exists to close. */
  const awaitingFonts = useRef(false);
  /** Waiters armed so far on this mount, against `MAX_FONT_REMEASURES`. Counted
   *  where the waiter is armed and not where it settles, so a load that never
   *  settles has still spent its turn. Never reset: a face that has loaded
   *  stays loaded for the document, so a mount sees a handful of real loads in
   *  its whole life however many imports pass through it. */
  const fontRemeasures = useRef(0);
  /** Set when the pass was skipped because a gesture was live. Flushed by
   *  whichever path releases the gesture, so the measurement that was owed
   *  actually happens — and NOT on every drag, which would spend a two-layout
   *  pass on the common path for nothing. */
  const skippedWhileDragging = useRef(false);
  const flushSkippedMeasurement = () => {
    if (!skippedWhileDragging.current) return;
    skippedWhileDragging.current = false;
    setRemeasureToken((token) => token + 1);
  };
  useLayoutEffect(() => {
    const table = tableRef.current;
    // No rows yet: measuring now would size every column from its HEADER alone
    // and open "Fit Score" at the width of the words "Fit Score".
    if (!table || contacts.length === 0) return;
    // A drag owns the `<th>` styles while it runs — see the docstring above.
    // The skip is REMEMBERED, because nothing about a gesture ending is a
    // dep: `resizeOrigin` is a ref, so `endResize` re-renders nothing. Without
    // this flag a measurement skipped mid-drag waits for the next unrelated
    // dep change — and if the gesture ended abnormally (pointer capture lost
    // with no pointerup/cancel, which `beginResize` already documents), the
    // stale origin would disable measurement for the whole session.
    if (resizeOrigin.current) {
      skippedWhileDragging.current = true;
      return;
    }
    // The rows on screen belong to the PREVIOUS import. Measuring them would
    // read A's content and stamp it with B's signature, after which B's real
    // rows arrive to an early return and B is latched forever. A SKIP, not a
    // key: putting row identity in the signature would re-open the per-page
    // jumping this latch exists to stop.
    if (rowsArePlaceholder) return;
    const signature = [
      // No `?? ""`, still — but the reason changed and the old one is now
      // false. It read "`importId` is a required, non-nullable prop, so the
      // fallback was unreachable". It is required on the DETAIL arm only: the
      // `preview` arm of `ContactsTableSurfaceProps` makes it optional, and
      // `ScoresTable` passes `importId ?? undefined`, so entry 0 IS `undefined`
      // whenever no import is selected. That is harmless — each mount owns its
      // `measuredFor` ref, so an empty entry cannot collide across imports —
      // but the claim of unreachability was load-bearing evidence in a file
      // whose comments get cited as such, so it is corrected rather than left.
      importId,
      remeasureToken,
      rationaleFor ? "rationales" : "no-rationales",
      // `onSort` changes what the HEADER renders — `col.label` alone versus a
      // button carrying the label plus a `↕` glyph and a gap, ~15px wider. It
      // flips when `allContactsQuery` resolves (`clientMode`), which is a
      // third query on a third schedule. Demonstrated by `/pr-review` Agent A,
      // who showed the flip does not currently re-measure. Small — MIN_WIDTH
      // absorbs 15px for any label under ~104px — but it is the same shape as
      // the two latch bugs already paid for, and it is one array entry.
      sortable ? "sortable" : "static",
      // Label as well as key: the header renders `col.label`, so it feeds the
      // `<th>`'s max-content and is an input to the measured value. The case it
      // covers is INTRA-import — one import changing a column's label while
      // keeping its key — because `importId` is entry 0 of this same array and
      // already separates any two imports from each other, whatever their
      // labels do. An earlier version of this comment justified the entry by
      // the cross-import case; that scenario cannot occur, and the mistake is
      // instructive: the entry was right, the reason attached to it was not,
      // and it arrived attached to a correct fix (`/pr-review` Agent C, who
      // proposed it and then withdrew the reasoning). Identical to the key
      // today only because `build_annex_columns` sets
      // `key = label = criterion_name` — a backend coupling asserted in a
      // comment and enforced nowhere in the FE types.
      columns
        .map((col) => `${col.key}\u0000${col.label}`)
        .join("\u0000"),
    ].join("\u0001");
    if (measuredFor.current === signature) return;
    const live = new Set(columns.map((col) => col.key));
    const measured: Record<string, number> = {};
    // SWALLOWED ON PURPOSE. `measureColumnWidths` restores what it touched in a
    // `finally` and has a test for doing so on a throw — which means the module
    // treats a throw as reachable. A throw out of a `useLayoutEffect` unmounts
    // the tree, so without this the contacts table would blank the page over a
    // failed WIDTH MEASUREMENT. Nothing is logged: there is no diagnostic here
    // a user or a log reader could act on, the restore has already happened,
    // and the fallback path below is the pre-measurement behaviour, which is
    // simply the old product.
    let raw: Readonly<Record<string, number>> = {};
    try {
      raw = measureColumnWidths(table);
    } catch {
      return;
    }
    for (const [key, px] of Object.entries(raw)) {
      // Filtered against the live descriptor list. Two jobs, and the second
      // is the load-bearing one: it drops a criterion that disappeared from
      // `columns` so no stale width survives it, AND it is what confines
      // measurement to the annex columns — a `<th>` that grows a
      // `data-col-key` without also being a descriptor contributes nothing.
      // `contact_info` is protected by this line, not only by not carrying
      // the attribute (verified by mutation: adding both the attribute and a
      // `pinnedFallback` still leaves it at 320px).
      if (live.has(key)) measured[key] = px;
    }
    // Only claim the signature once a measurement actually happened. In a DOM
    // with no layout engine every read is 0, `measured` is empty, and claiming
    // it would freeze the fallback in place for good.
    if (Object.keys(measured).length > 0) measuredFor.current = signature;
    setNaturalWidths((prev) => {
      const keys = Object.keys(measured);
      const same =
        keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === measured[k]);
      return same ? prev : measured;
    });
    // A face still loading means the pass above read the FALLBACK's metrics
    // (`font-display: swap`), and the latch would keep them for the session.
    // Asked after the pass because the pass is often the layout that requests
    // the face: see "Web-font load" in `measureColumnWidths.ts`. The bump goes
    // through the same effect, so the gesture skip, the placeholder skip and
    // the scroll restore all apply to the re-measure, and it ends the chain
    // itself: the next pass finds the set `loaded` and schedules nothing. The
    // count is for an engine where it does not: see `MAX_FONT_REMEASURES`.
    const fontLoad = pendingFontLoad(table.ownerDocument);
    if (fontLoad && !awaitingFonts.current && fontRemeasures.current < MAX_FONT_REMEASURES) {
      awaitingFonts.current = true;
      fontRemeasures.current += 1;
      const remeasure = () => {
        awaitingFonts.current = false;
        setRemeasureToken((token) => token + 1);
      };
      void fontLoad.then(remeasure, remeasure);
    }
  }, [columns, contacts, importId, rationaleFor, remeasureToken, rowsArePlaceholder, sortable]);

  /** The default width for a pinned column: measured, capped at what used to be
   *  its hardcoded default, floored at `MIN_WIDTH`. Falls back to the cap when
   *  there is no measurement (first render, or a DOM with no layout engine), so
   *  the worst case is exactly the old behaviour.
   *
   *  This hands the resize machinery a START WIDTH it could not previously
   *  receive: an annex column can now open at exactly `MIN_WIDTH`, which no
   *  annex column ever did while the default was 352. Re-verified at that
   *  boundary rather than assumed (`/pr-review`, Agent A §1) — `toleranceFor`
   *  picks the drag tolerance, `movesWrongWay(128, 128, "narrower")` is false,
   *  `final === startWidth`, and the commit lands in the `reactPinsWidth`
   *  branch and re-pins 128. No invariant moves; the diff not touching those
   *  functions is not by itself the argument. */
  const pinnedFallback = (key: string, cap: number) => fitWidth(naturalWidths[key], cap, MIN_WIDTH);

  /** Double-click: drop the override AND ask for a fresh measurement.
   *
   *  `resetWidth` alone was enough while the fallback was a constant. With a
   *  LATCHED measurement behind it, dropping the override lands the column on
   *  whatever was on screen the first time this signature was measured — so the
   *  gesture the PR sells as "auto-fit" could hand back a stale fit. Bumping
   *  the token re-measures once, which makes the sentence true again and, more
   *  usefully, gives the user a way out of any stale latch that a future input
   *  introduces. One pass, on an explicit gesture.
   *
   *  DELIBERATELY NOT PER-COLUMN: the token is global, so double-clicking any
   *  handle — including Name's or Contact info's, which are not measured at
   *  all — re-fits every un-overridden annex column, not just the one under
   *  the cursor. Almost always invisible, occasionally a small surprise ("I
   *  double-clicked Marketing and Leadership moved"). Left global because the
   *  measurement pass is per-table by construction and a per-column token
   *  would buy precision nobody asked for at the cost of another cache key. */
  // The AI Review preview shows a five-row sample of a run that has not been
  // applied: nothing here is exported, compared, enriched or selectable yet.
  // The four outcome columns and both filter bars are therefore not "hidden
  // for tidiness" — they would report on events that have not happened.
  const isPreview = variant === "preview";
  // The Combine preview's sortable Location / Thrive ID headers announce
  // their sort state the same way the Name header does (bug hunt
  // 2026-09-23, F11): "none" while sortable but inactive, nothing when the
  // table is not sortable at all.
  const previewAriaSort = (key: string): "ascending" | "descending" | "none" | undefined => {
    if (!onSort) return undefined;
    if (sort?.key !== key) return "none";
    return sort.dir === "desc" ? "descending" : "ascending";
  };
  // The person-panel handle of a row: null when the panel entry is off or the
  // row has no LinkedIn profile to open.
  const personHandleOf = (row: ImportContactRow): string | null =>
    onOpenPerson ? linkedinHandle(row.linkedin_url) : null;
  const isPersonOpen = (row: ImportContactRow): boolean =>
    openPersonHandle !== null &&
    personHandleOf(row) === openPersonHandle &&
    (openPersonRowId === null || row.import_contact_id === openPersonRowId);
  // The click target is the Name cell (`PersonRowEntry`); hover and the open
  // state are painted on the WHOLE row (row-CTA epic, sub-task 02): the `tr`
  // fill for the ordinary cells, and the two frozen cells, which keep their
  // opaque surface (rows scroll under them), follow through
  // `FROZEN_CELL_TINT_CLASS` and carry the accent stripe on the first of
  // them. `focus-within` mirrors the hover so a keyboard user tabbing onto
  // the row button sees the same row-wide tint. A row is a hover target only
  // when it is an entry: the tint promises a click, and a row with no handle
  // has nothing to open (its cell says so with a chip instead).
  const isDimmed = (row: ImportContactRow): boolean =>
    dimUnselectedRows && !selectedIds.has(row.import_contact_id);
  const rowStateClass = (row: ImportContactRow): string =>
    [
      isDimmed(row) ? "[&>td:not(.sticky)]:opacity-50" : "",
      personHandleOf(row) !== null ? "group/row hover:bg-row-hover focus-within:bg-row-hover" : "",
      isPersonOpen(row) ? "bg-row-hover" : "",
    ]
      .filter(Boolean)
      .join(" ");
  // `first`: the stripe goes on the row's FIRST frozen cell — the checkbox
  // cell on `ImportDetailPage`, the Name cell on the AI Review page's preview
  // table (no selection column there).
  const frozenCellStateClass = (row: ImportContactRow, first: boolean): string =>
    [
      FROZEN_CELL_TINT_CLASS,
      isPersonOpen(row) ? FROZEN_CELL_TINT_ON_CLASS : "",
      first && isPersonOpen(row) ? FROZEN_CELL_STRIPE_CLASS : "",
    ]
      .filter(Boolean)
      .join(" ");
  // Name is the frozen column. It clears the `w-12` checkbox column
  // (`left-12`) whenever that column renders — on `variant="detail"` always,
  // and on `variant="preview"` only when `showSelectionColumn` opts it in
  // (round 2, sub-task R2-B). Otherwise (the AI Review preview, no checkbox
  // column) anchoring at `left-12` would freeze Name 3rem from the edge and
  // leave a gap that only appears once the table scrolls horizontally.
  const showSelection = !isPreview || showSelectionColumn;
  const nameStickyLeft = showSelection ? "left-12" : "left-0";
  // The frozen Name column had no right edge, so a column scrolling under it
  // read as CUT (local QA, PR #1016: "Thrive ID cortada"). Once the wrapper
  // is scrolled sideways, the frozen cells draw an edge + soft shadow.
  const [scrolledX, setScrolledX] = useState(false);
  const frozenEdgeClass = scrolledX ? FROZEN_EDGE_SHADOW_CLASS : "";

  /**
   * The rightmost column, which absorbs whatever width the others do not use.
   *
   * THE TABLE ALWAYS SPANS ITS CONTAINER. Distributing the surplus across every
   * fluid column is what made a short list look bloated in the first place —
   * measured, Name at 466px where its content wants 240 — so the surplus goes
   * to exactly one column instead of all of them. Everything else renders at
   * its own width, and the box has no dead gutter.
   *
   * Which column that is, in render order: the last Company Map column on a BCT
   * import, else the last AI-Review criterion, else Contact info.
   *
   * Since 2026-09-15 the reserved `AI Review Total` pins LEFT of the criteria,
   * so this resolves to a criterion (a wide rationale cell) rather than to the
   * narrow Total, which is the better home for the surplus. The one-criterion
   * and zero-criterion runs still work: with no criteria at all the sole annex
   * column IS the Total and it absorbs the surplus, as before.
   *
   * No preview arm, deliberately. One briefly existed — `isPreview ? "name"` —
   * to cover "a preview with no annex columns", on the reasoning that Contact
   * info is not rendered there so the surplus would go to nobody. That state
   * is unreachable: `criteriaToAnnexColumns` unconditionally emits the
   * reserved `AI Review Total` descriptor, so a preview always has at least
   * one annex column and the branch above always wins. A guard that cannot
   * fire is worse than none, because it will be read as covering a case it
   * does not.
   */
  // On a Thrive-sourced list the three candidacy columns follow Contact info,
  // so the last of them takes the surplus when no annex column follows (04).
  const showThriveColumns = !isPreview && thriveColumns !== undefined;
  // The stage chips' counts. The server lists only the stages present under
  // the other filters, so the ACTIVE stage can drop out (type a name nobody
  // in it has); it stays, at 0, so the recruiter can still press it off.
  const stageChipCounts =
    stageFilter && !(counts?.stages ?? []).some((b) => b.label === stageFilter)
      ? [...(counts?.stages ?? []), { label: stageFilter, count: 0 }]
      : (counts?.stages ?? []);
  const lastColumnKey = isBct
    ? "bct_years"
    : columns.length > 0
      ? columns[columns.length - 1].key
      : showThriveColumns
        ? "thrive_added"
        : "contact_info";

  /**
   * Width for the fill column: take the surplus, but never drop below the width
   * the column would otherwise have had.
   *
   * The floor is the whole trick and it was NOT obvious — `width: 100%` alone
   * looks right on a wide screen and is a regression on a narrow one. Measured
   * live: with the wrapper at 1500px Contact info fills to 945, at 900px to
   * 345, and at 600px — where the table has to scroll — it COLLAPSES TO 65px
   * without the floor. That is below its own 320 pin and straight back into
   * the shredded-address defect cl.4 item 8 exists to prevent. With
   * `minWidth` set to its own width it holds 320 and the table scrolls, which
   * is correct.
   *
   * `maxWidth: none` because `widthStyle`'s pin sets max == width, and a pinned
   * max would cap the fill at the pin.
   */
  const fillStyle = (own: number | undefined) => ({
    width: "100%",
    // ALWAYS a floor. This used to be `own == null ? {} : …`, and that escape
    // hatch existed for exactly one call site — `bct_years`, which is fluid
    // until someone drags it, so `widthFor` answers `undefined` and the column
    // shipped as a bare `width: 100%`. That is precisely the shape this
    // function's docstring calls a regression: it collapses to min-content the
    // moment the table has to scroll. The hole was encoded in the signature
    // rather than closed (`/pr-review` round 8, Agent A). `MIN_WIDTH` is the
    // right default because it is the floor every other width in this file
    // already respects.
    minWidth: `${own ?? MIN_WIDTH}px`,
    maxWidth: "none",
  });

  const resetAndRefit = (key: string) => {
    resetWidth(key);
    setRemeasureToken((token) => token + 1);
  };

  const resizeHandlers: ResizeHandlers = {
    begin: beginResize,
    move: moveResize,
    end: endResize,
    cancel: cancelResize,
    reset: resetAndRefit,
    key: keyResize,
  };

  // Filtering happens server-side; `contacts` already arrives narrowed. The
  // whole-import chip counts ride on the response (`counts.review` — sub-task
  // 10's block; the flat legacy buckets beside it are no longer read here) —
  // 0s while the first page is still loading keep the chip labels stable.
  const failedCount = counts?.enrichment_failed ?? 0;
  // Only selectable contacts feed select-all: not already in Thrive
  // (exported / already-exported) and not a W1 duplicate; failed is retryable,
  // W2 can Update-anyway. Shared predicate with the Export panel. Scoped to the
  // currently-visible (filtered) rows so select-all matches what's on screen.
  const selectableIds = contacts.filter(isExportable).map((c) => c.import_contact_id);
  const inAllFilteredMode = selectionMode === "all-filtered";
  const allSelected =
    inAllFilteredMode ||
    (selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id)));
  // The Gmail-style escalation banner (sub-task 05). OFFER it once the page is
  // fully selected by hand and the filtered set is larger than what is on
  // screen; CONFIRM it while the cross-page selection is active. N is always
  // the server `total` for the ACTIVE filters — the `contacts` prop arrives
  // already filtered, so the response total is the filtered universe, and the
  // parent clears the whole selection the moment a filter or the dataset
  // changes (stale-N rule, `useContactsSelection`).
  const offerSelectAllBanner =
    !isPreview &&
    !inAllFilteredMode &&
    allSelected &&
    totalFilteredContacts !== undefined &&
    totalFilteredContacts > contacts.length;
  const showAllFilteredBanner =
    !isPreview && inAllFilteredMode && totalFilteredContacts !== undefined;

  return (
    <div className="flex flex-col gap-3">
      {!isPreview && (
        <div className="flex flex-row flex-wrap items-center gap-3">
          <div className="flex flex-row flex-wrap items-center gap-2">
            {/* "Status:", per the approved 2026-09-07 mock — the bar now
                filters on the ONE server-derived `review_status`, not on the
                write-outcome column alone, so borrowing that column's header
                (`writeOutcomeLabel`) would name only part of what the chips
                cover. */}
            <span className="text-label text-text-secondary">Status:</span>
            <ReviewStatusFilterBar
              value={reviewStatusFilter}
              onChange={onReviewStatusFilterChange}
              dupLinkedin={dupLinkedinFilter}
              onDupLinkedinChange={onDupLinkedinFilterChange}
              counts={counts?.review}
              thriveId={thriveIdFilter}
              onThriveIdChange={onThriveIdFilterChange}
              addToSearch={addToSearchFilter}
              onAddToSearchChange={onAddToSearchFilterChange}
              skipped={skippedFilter}
              onSkippedChange={onSkippedFilterChange}
            />
          </div>
          <span className="text-text-secondary" aria-hidden="true">
            |
          </span>
          <div className="flex flex-row flex-wrap items-center gap-2">
            <span className="text-label text-text-secondary">Enrich Data:</span>
            <EnrichmentFilterBar
              value={enrichmentFilter}
              onChange={onEnrichmentFilterChange}
              failedCount={failedCount}
            />
          </div>
          {showThriveColumns && onStageFilterChange && stageChipCounts.length > 0 && (
            <>
              <span className="text-text-secondary" aria-hidden="true">
                |
              </span>
              <div
                data-testid="stage-filter"
                className="flex flex-row flex-wrap items-center gap-2"
              >
                <span className="text-label text-text-secondary">Origin stage:</span>
                {/* The Candidates tab's chips, one stage at a time here: the
                    API filters by a single `stage`. The counts come from the
                    list (`counts.stages`), under every other active filter. */}
                <StageChips
                  distribution={stageChipCounts}
                  selected={stageFilter ? [stageFilter] : []}
                  onToggle={(stage) => onStageFilterChange(stageFilter === stage ? null : stage)}
                />
              </div>
            </>
          )}
        </div>
      )}
      {/* One filter LINE: name search first, then the general "Current Position
          Filters", then — on a BCT import only — the format-specific "Company
          Map Filters", separated by a vertical rule. Rendered HERE rather than
          by the page so that ordering is structural — the sections cannot
          drift apart.

          ONE LINE IS WIDTH-DEPENDENT, and the threshold is high. The BCT row
          WITHOUT the name search needed ~1258px of CONTENT width, holding from
          about a 1580px viewport up and wrapping below that (measured in
          Chromium — 1280/1440/1512 all wrapped). The name search joined this
          row on 2026-08-25 and adds its fixed `w-80` plus one `gap-3`: **332px,
          for ~1590px of content / roughly a 1910px viewport**.

          That sounds like a regression and is the opposite of one. The name
          search used to occupy a FULL ROW OF ITS OWN below these, so the row
          COUNT went DOWN: a Company Map import renders 2 filter rows where it
          used to render 3, and a raw / AI-review import renders 1 where it
          used to render 2. What changed is only WHICH group wraps — name +
          Current Position stay together on the first line at every common
          width, and Company Map wraps below exactly as it already did.

          Treat every figure here as approximate: the two Current-Position and
          two Company-Map inputs carry no width class, so their size is
          font-metric-derived, and the app declares Inter without shipping an
          `@font-face` — the threshold moves a little per machine. Only the name
          search is exact (`w-80`). THIS COMMENT IS THE SINGLE SOURCE for the
          figures; the e2e spec brackets them far enough out (1800 / 1280) that
          drift cannot flip the assertions.

          Wrapping is therefore the NORMAL state on a laptop, not a rare edge
          case, which is why the separator is a `border-l` on the group rather
          than a `|` glyph between the groups: a character divider ends up
          orphaned on one line or the other once the row breaks, while the rule
          stays attached to the filters it introduces in both states. */}
      {!isPreview && (
        <div className="flex flex-row flex-wrap items-center gap-3">
          <NameSearchBar nameQuery={nameQuery} onNameQueryChange={onNameQueryChange} />
          {onNotesQueryChange ? (
            <NotesSearchBar
              notesQuery={notesQuery}
              onNotesQueryChange={onNotesQueryChange}
              matchCount={notesMatchCount}
            />
          ) : null}
          {onEmailFilterChange ? (
            <input
              type="text"
              data-testid="email-filter"
              aria-label="Filter by email"
              placeholder="Filter by email…"
              value={emailFilter}
              onChange={(e) => onEmailFilterChange(e.target.value)}
              className={filterInputClass}
            />
          ) : null}
          <ContactFilterBar
            currentCompanyFilter={currentCompanyFilter}
            onCurrentCompanyFilterChange={onCurrentCompanyFilterChange}
            currentPositionFilter={currentPositionFilter}
            onCurrentPositionFilterChange={onCurrentPositionFilterChange}
          />
          {isBct && (
            <fieldset
              data-testid="bct-filters"
              className="flex flex-row flex-wrap items-center gap-3 border-l border-border pl-3"
            >
              <legend className="sr-only">Company Map Filters</legend>
              {/* The row SHOWS every placing position collapsed (titles
                  joined, merged years), but the filters match ONE position
                  at a time: company, position and years together (PR #982
                  review iter 2, m-8). Said on hover, and to screen readers. */}
              <span
                aria-hidden="true"
                data-testid="bct-filters-label"
                title="Each filter matches a single position: company, position and years together."
                className="text-label text-text-secondary"
              >
                Company Map Filters:
              </span>
              <span className="sr-only">
                Each filter matches a single position: company, position and years together.
              </span>
              <input
                type="text"
                data-testid="bct-company-filter"
                aria-label="Filter by Company Map matched company"
                placeholder="Filter by company…"
                value={bctCompanyFilter}
                onChange={(e) => onBctCompanyFilterChange(e.target.value)}
                className={filterInputClass}
              />
              <input
                type="text"
                data-testid="bct-position-filter"
                aria-label="Filter by Company Map matched position"
                placeholder="Filter by position…"
                value={bctPositionFilter}
                onChange={(e) => onBctPositionFilterChange(e.target.value)}
                className={filterInputClass}
              />
              <div className="flex items-center gap-1.5">
                <span className="text-label text-text-secondary">Years:</span>
                <input
                  type="number"
                  data-testid="bct-year-from"
                  aria-label="Filter year from"
                  placeholder="From"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={bctYearFrom}
                  onChange={(e) => onBctYearFromChange(e.target.value)}
                  className="w-20 rounded-sm border border-border bg-surface px-2 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
                />
                <span className="text-text-muted" aria-hidden="true">
                  –
                </span>
                <input
                  type="number"
                  data-testid="bct-year-to"
                  aria-label="Filter year to"
                  placeholder="To"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={bctYearTo}
                  onChange={(e) => onBctYearToChange(e.target.value)}
                  className="w-20 rounded-sm border border-border bg-surface px-2 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
                />
              </div>
            </fieldset>
          )}
        </div>
      )}
      {/* Cross-page select-all banner (sub-task 05). Two states, one slot:
          the OFFER (page fully selected, more matches beyond it) and the
          CONFIRMATION (cross-page selection active). Buttons are text links
          in the accent colour, Gmail's own affordance for this exact banner.
          Tokens only — no inline styles. */}
      {offerSelectAllBanner && (
        <div
          data-testid="select-all-banner"
          className="flex flex-row flex-wrap items-center gap-2 rounded-md border border-border bg-bg-subtle px-4 py-2 text-body-sm text-text-secondary"
        >
          <span>
            All {selectableIds.length} selectable contact{selectableIds.length === 1 ? "" : "s"} on
            this page are selected.
          </span>
          <button
            type="button"
            data-testid="select-all-across-pages"
            onClick={onSelectAllFiltered}
            className="text-accent hover:text-accent-hover hover:underline"
          >
            Select all {totalFilteredContacts} contacts
          </button>
        </div>
      )}
      {showAllFilteredBanner && (
        <div
          data-testid="select-all-banner"
          className="flex flex-row flex-wrap items-center gap-2 rounded-md border border-border bg-bg-subtle px-4 py-2 text-body-sm text-text-secondary"
        >
          <span>
            All {totalFilteredContacts} contact{totalFilteredContacts === 1 ? "" : "s"} matching the
            current filters are selected.
          </span>
          <button
            type="button"
            data-testid="clear-cross-page-selection"
            onClick={onClearCrossPageSelection}
            className="text-accent hover:text-accent-hover hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}
      {contacts.length === 0 ? (
        <p
          data-testid="contacts-filter-empty"
          className="rounded-md border border-border bg-bg-subtle px-4 py-3 text-body-sm text-text-secondary"
        >
          {/* No preview arm. `ScoresTable` intercepts every `rows.length === 0`
              state before `<ResultsTable>` is reached — pending, failed,
              vanished — so `variant="preview"` never receives an empty
              `contacts`. A preview-specific string here could not fire, and
              would contradict `ContactsVanished`, the branch that actually owns
              that state. Same argument as `lastColumnKey`'s missing preview
              arm: a guard that cannot fire is worse than none.

              No "every row was skipped" arm either, for the same reason. A
              `ready` import with 0 contacts and a skipped count cannot be
              produced: the worker ends a file the row rule emptied `failed`
              (`all_rows_skipped_reason`, since 2026-09-28), and an import
              parsed before the rule has no count at all. An arm for it lived
              here for one review iteration and was retired
              (/pr-review #971 iter 2, Agent C M7). */}
          No contacts match the current filters.
        </p>
      ) : (
        <div
          className="max-h-[70vh] overflow-auto rounded-md border border-border bg-table-surface"
          data-testid="contacts-table-scroller"
          onScroll={(e) => {
            const next = e.currentTarget.scrollLeft > 0;
            if (next !== scrolledX) setScrolledX(next);
          }}
        >
          {/* THE TABLE ALWAYS SPANS ITS CONTAINER, and the surplus goes to ONE
              column — see `lastColumnKey`.

              This replaces a shipped design that did the opposite, and the
              history is worth keeping because both halves of it were measured.
              The original defect was `min-w-full` handing the surplus to every
              FLUID column at once: measured in Chromium, a 4-column table in a
              1400px box gave Name 479px against a content width of 234, and a
              one-word criterion 246px against 120. On the live app the same
              mechanism took "Added to search" 59 -> 122px as the container grew.

              The first fix was to stop the table stretching at all —
              `w-fit max-w-full` on this wrapper so the box hugged the table.
              That did remove the bloat, and it cost 544px of dead page beside
              the box at a 2080px container on the ordinary 7-column list, and
              made this the only table in the app that did not span its
              container. Directing the surplus at the last column instead
              removes the dead gutter without re-inflating every column — but
              NOT "at their content widths", which an earlier draft claimed
              four lines above the paragraph that contradicts it. The other
              fluid columns are squeezed toward min-content, and min-content is
              not content width:
              measured live on an AI-Review import, Name 466 -> 240.
              (An earlier draft added "with the row height dropping 108 -> 90px,
              because the column that absorbed the slack wraps less". WITHDRAWN:
              that read ONE row. The mean over all 98 goes 77.4 -> 81.8 — rows
              get TALLER, because the columns squeezed toward min-content wrap
              more than the fill column stops wrapping. The retraction was
              written into the PRD and the commit message and not here, which is
              the copy a maintainer reads first.)

              WHAT IT COSTS, stated because it is real: a `width: 100%` cell
              squeezes the REMAINING fluid columns toward their min-content. On
              the AI-Review list that was the point (466 was bloat). On a raw
              import it took "Added to search" 110 -> 59px, which is tighter
              than it was.

              Two candidates rejected on evidence, both still worth not
              retrying: a trailing `width: 100%` FILLER cell starves every
              unpinned column to min-content (Name 105px, rows 474px tall)
              because it has no content of its own to hold a floor; and a CSS
              `max-width` cap on a `<th>` is ignored under slack and
              over-applied without it. */}
          <table ref={tableRef} data-testid="contacts-table" className="min-w-full border-collapse">
            {/* Sticky header: the wrapper is a bounded (max-h) scroll region so
                `sticky top-0` on the header keeps the column titles pinned while
                the rows scroll under them. `border-collapse` drops a sticky
                cell's own border on scroll, so the divider is a box-shadow on
                the header cells (`shadow-[...]`) rather than a `border-b`. */}
            <thead className="sticky top-0 z-20 bg-surface shadow-[inset_0_-1px_0_var(--border)]">
              <tr className="bg-surface text-left text-caption text-text-muted">
                {/* Frozen left columns (checkbox + Name): `sticky left-0` keeps
                    them pinned during horizontal scroll. The header corner cells
                    sit at z-30 (above the z-20 header and z-10 frozen body cells);
                    `w-12` fixes the checkbox column width so Name's `left-12`
                    offset lines up exactly. */}
                {showSelection && (
                  <th className="sticky left-0 top-0 z-30 w-12 min-w-[3rem] bg-surface px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      data-testid="select-all"
                      checked={allSelected}
                      disabled={selectableIds.length === 0}
                      onChange={(e) => onToggleSelectAll(selectableIds, e.target.checked)}
                    />
                  </th>
                )}
                {(() => {
                  // Name column: sortable via the SAME `sort`/`onSort` mechanism
                  // the annex/score columns use (client ask: also sort by name).
                  const active = sort?.key === NAME_SORT_KEY;
                  return (
                    <th
                      // `min-w-` and NOT a width: Name stays the one fluid
                      // column (it takes whatever the pinned ones leave), but
                      // it now has a FLOOR. Without one, restoring the pinned
                      // 320px Contact info column pushed the table past its
                      // wrapper, and `table-layout: auto` pays for that out of
                      // the only fluid column — measured: Name at 99px, a
                      // never-truncated job title wrapping inside it, and a
                      // row 1032px tall. Overflowing into the wrapper's
                      // horizontal scroll is the better trade; that is what
                      // the scroll is for.
                      className={`relative sticky ${nameStickyLeft} top-0 z-30 min-w-[15rem] bg-surface px-3 py-2 ${frozenEdgeClass}`}
                      style={widthStyle("name")}
                      data-testid="name-header"
                      data-sort={active ? sort?.dir : undefined}
                      // Sortable-but-inactive columns announce "none" so a
                      // screen reader knows they can be sorted; plain (non-AI)
                      // imports render inert text (no `onSort`) → no aria-sort.
                      aria-sort={
                        active
                          ? sort?.dir === "asc"
                            ? "ascending"
                            : "descending"
                          : onSort
                            ? "none"
                            : undefined
                      }
                    >
                      {onSort ? (
                        <button
                          type="button"
                          data-testid="name-sort"
                          className="flex w-full items-center gap-1 hover:text-text"
                          onClick={() => onSort(NAME_SORT_KEY)}
                        >
                          Name
                          <span
                            aria-hidden="true"
                            data-testid="name-sort-icon"
                            className={active ? "text-accent" : "text-text-muted"}
                          >
                            {active ? (sort?.dir === "desc" ? "▼" : "▲") : "↕"}
                          </span>
                        </button>
                      ) : (
                        "Name"
                      )}
                      <ColumnResizeHandle
                        columnKey="name"
                        label="Name"
                        width={widthFor("name")}
                        handlers={resizeHandlers}
                      />
                    </th>
                  );
                })()}
                {/* Location — `richPreview` only (round 2, sub-task
                    R2-B): Combine & Filter's Review-contacts table needs them,
                    the detail page does not carry a Location column at all
                    (`person_profile.location` has no home there today) and
                    already links the Name cell to LinkedIn instead of a
                    separate column. Deliberately NOT wired into the
                    drag-resize / width-measurement machinery the base columns
                    share (`useColumnWidths`, `ColumnResizeHandle`) — that
                    system is scoped to the columns already persisted under
                    `localStorage["nu-assist:web:contacts-column-widths"]`,
                    and it is new, `richPreview`-only, and fixed-width
                    by design. */}
                {isPreview && richPreview && (
                  <th
                    className="px-3 py-2"
                    data-testid="location-header"
                    aria-sort={previewAriaSort("location")}
                  >
                    {onSort ? (
                      <button
                        type="button"
                        data-testid="location-sort"
                        className="flex items-center gap-1 hover:text-text"
                        onClick={() => onSort("location")}
                      >
                        Location
                        <span aria-hidden="true" className="text-text-muted">
                          {sort?.key === "location" ? (sort.dir === "desc" ? "▼" : "▲") : "↕"}
                        </span>
                      </button>
                    ) : (
                      "Location"
                    )}
                  </th>
                )}
                {/* `columnKey` stays "export" — it is the persistence key in
                    `localStorage["nu-assist:web:contacts-column-widths"]`, so
                    renaming it would orphan every width a recruiter has set.
                    The user-facing label is what changed. */}
                {!isPreview && (
                  <th className="relative px-3 py-2" style={widthStyle("export")}>
                    {writeOutcomeLabel}
                    <ColumnResizeHandle
                      columnKey="export"
                      label={writeOutcomeLabel}
                      width={widthFor("export")}
                      handlers={resizeHandlers}
                    />
                  </th>
                )}
                {/* Thrive ID closes the BASE block, identical across all three
                    list types (cl.4 items 7 + 14 as clarified 2026-08-20: the
                    base columns pin LEFT, and everything type-specific follows).
                    It used to sit to the RIGHT of the dynamic annex columns, so
                    its position moved per list. On the preview variant it also
                    renders under `richPreview` (round 2, sub-task R2-B) — the
                    AI Review preview never passes that flag and keeps seeing
                    no Thrive ID column at all, unchanged. */}
                {(!isPreview || richPreview) && (
                  <th
                    className="relative px-3 py-2"
                    style={widthStyle("thrive_id")}
                    data-testid="thrive-id-header"
                    aria-sort={isPreview ? previewAriaSort("thrive") : undefined}
                  >
                    {/* The EXPLANATION, once, in the header — paired with the
                      amber dashes in the cells below.

                      `compareHasRun` is a property of the IMPORT, so when it is
                      false every cell in this column is in the same state. A
                      per-row warning icon would stamp the same sentence a
                      hundred times, and a filled amber column would spend the
                      loudest colour in the system (it means "conflict, act now")
                      on a column-level fact. So: one icon that says WHAT is
                      missing, the LABEL tinted to match so the column reads as
                      unfinished, and the cells' dashes in the same amber so the
                      eye links all three.

                      The hover target is the icon AND the label together, not
                      the icon alone: the two are one statement, and a tooltip
                      that only answers on a 14px glyph is a tooltip most people
                      never find. A text pill lived here for one round instead
                      and blew the header open — two lines tall, pushing
                      "Contact info" sideways. */}
                    {isPreview && richPreview && onSort ? (
                      // Combine preview (round 2): server-side sort by Thrive
                      // status, then ID, same `onSort` channel as Name /
                      // Location. Labelled "Thrive", not "Thrive ID" (PR #962
                      // follow-up F7): the order groups by status first, so
                      // "Thrive ID ▲" over 6020, 6009, 944 read as broken.
                      <button
                        type="button"
                        data-testid="thrive-sort"
                        title="Sort by Thrive status (in this search, in Thrive, not found, not checked), then Thrive ID"
                        className="flex items-center gap-1 hover:text-text"
                        onClick={() => onSort("thrive")}
                      >
                        Thrive
                        <span aria-hidden="true" className="text-text-muted">
                          {sort?.key === "thrive" ? (sort.dir === "desc" ? "▼" : "▲") : "↕"}
                        </span>
                      </button>
                    ) : compareHasRun ? (
                      <span className="flex items-center gap-1.5">Thrive ID</span>
                    ) : (
                      <Tooltip
                        side="bottom"
                        align="start"
                        clickToPin
                        maxWidthClassName="max-w-[240px]"
                        // In the header, so it opens DOWNWARD into the rows and
                        // was never clipped vertically. Opted in anyway: the
                        // same container scrolls HORIZONTALLY, so a wide bubble
                        // on a wide table can still meet its right edge, and a
                        // rule of "every tooltip inside the scroll box escapes
                        // it" is one a reader can check.
                        escapesOverflow
                        content={
                          <span className="text-left font-normal">
                            Compare to Thrive has not been run for this list, so nothing here has
                            been looked up yet.
                          </span>
                        }
                      >
                        <button
                          type="button"
                          data-testid="thrive-not-compared-header"
                          // The visible label ("Thrive ID") MUST be contained in
                          // the accessible name — WCAG 2.5.3 Label in Name (A).
                          // Without the prefix, voice control ("click Thrive ID")
                          // cannot reach this button, and the column's own name
                          // disappears from the header's accessible name
                          // (`/pr-review` iter 4, Agent A §3(a) + Agent B A).
                          aria-label="Thrive ID. Compare to Thrive has not been run for this list."
                          className="flex items-center gap-1.5 rounded-sm text-warning-ink focus:outline-none focus:ring-1 focus:ring-accent"
                        >
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 16 16"
                            className="h-3.5 w-3.5 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M8 2.5 14.5 13.5h-13L8 2.5Z" />
                            <path d="M8 6.5v3" />
                            <path d="M8 11.6v.01" />
                          </svg>
                          Thrive ID
                        </button>
                      </Tooltip>
                    )}
                    <ColumnResizeHandle
                      columnKey="thrive_id"
                      label="Thrive ID"
                      width={widthFor("thrive_id")}
                      handlers={resizeHandlers}
                    />
                  </th>
                )}
                {/* Lists — `richPreview` only. Which SOURCE people lists this
                    merged row came from (`sourceListsFor`); has no detail-page
                    counterpart (a detail row belongs to exactly one list). */}
                {isPreview && richPreview && (
                  <th className="px-3 py-2" data-testid="lists-header">
                    Lists
                  </th>
                )}
                {isPreview && richPreview && previewStageColumn && (
                  <th className="px-3 py-2" data-testid="preview-stage-header">
                    Stage
                  </th>
                )}
                {/* "Contact info" closes the BASE block (cl.4 items 7 + 8). It
                    used to be called "Enrich Data" and used to sit LAST, after
                    the dynamic columns — so its position moved per list type,
                    which is the thing item 7 exists to end.
                    The `CONTACT_INFO_WIDTH` fallback is item 8's "wider by
                    default" and is NOT cosmetic: passing a fallback to
                    `widthStyle` is what pins this column, and a pinned column
                    is not sized from its content — which is the entire fix for
                    the shredded address. Drop the second argument and the
                    column collapses to ~111px again (measured).
                    DELIBERATELY NOT content-fitted, unlike the annex columns
                    next to it. "Wider by default" is item 8's ask in its own
                    words; auto-fitting this column would answer a request for
                    a wide column by making it narrow whenever the seeded
                    addresses happen to be short. 320 stays a value, not a cap.
                    Both the style and the handle now read ONE variable — they
                    used to repeat `?? CONTACT_INFO_WIDTH` independently, which
                    is precisely the drift `widthStyle`'s lockstep note says no
                    test would catch. */}
                {!isPreview &&
                  (() => {
                    const emailActive = sort?.key === EMAIL_SORT_KEY;
                    return (
                      <th
                        data-testid="enrich-data-header"
                        className="relative px-3 py-2"
                        style={
                          lastColumnKey === "contact_info"
                            ? fillStyle(widthFor("contact_info") ?? CONTACT_INFO_WIDTH)
                            : widthStyle("contact_info", CONTACT_INFO_WIDTH)
                        }
                        aria-sort={
                          emailActive
                            ? sort?.dir === "asc"
                              ? "ascending"
                              : "descending"
                            : onSort
                              ? "none"
                              : undefined
                        }
                      >
                        {onSort ? (
                          <button
                            type="button"
                            data-testid="email-sort"
                            className="flex w-full items-center gap-1 hover:text-text"
                            onClick={() => onSort(EMAIL_SORT_KEY)}
                          >
                            Contact info
                            <span
                              aria-hidden="true"
                              className={emailActive ? "text-accent" : "text-text-muted"}
                            >
                              {emailActive ? (sort?.dir === "desc" ? "▼" : "▲") : "↕"}
                            </span>
                          </button>
                        ) : (
                          "Contact info"
                        )}
                        <ColumnResizeHandle
                          columnKey="contact_info"
                          label="Contact info"
                          isFill={lastColumnKey === "contact_info"}
                          width={widthFor("contact_info") ?? CONTACT_INFO_WIDTH}
                          handlers={resizeHandlers}
                        />
                      </th>
                    );
                  })()}
                {showThriveColumns && thriveColumns && (
                  <>
                    <ThriveColumnHeader
                      columnKey="thrive_stage"
                      label="Origin stage"
                      sortKey={STAGE_SORT_KEY}
                      hint={`Stage in ${originSearchPhrase(thriveColumns)}, ${asOfPhrase(thriveColumns, "stage")}.`}
                      sort={sort}
                      onSort={onSort}
                      style={widthStyle("thrive_stage")}
                      width={widthFor("thrive_stage")}
                      handlers={resizeHandlers}
                    />
                    <ThriveColumnHeader
                      columnKey="thrive_in_stage"
                      label="In stage"
                      sortKey={IN_STAGE_SORT_KEY}
                      hint={`Days in that stage in ${originSearchPhrase(thriveColumns)}, ${asOfPhrase(thriveColumns, "days")}.`}
                      sort={sort}
                      onSort={onSort}
                      style={widthStyle("thrive_in_stage")}
                      width={widthFor("thrive_in_stage")}
                      handlers={resizeHandlers}
                    />
                    <ThriveColumnHeader
                      columnKey="thrive_added"
                      label="Added"
                      sortKey={ADDED_SORT_KEY}
                      hint={`When the candidate was added to ${originSearchPhrase(thriveColumns)}.`}
                      sort={sort}
                      onSort={onSort}
                      style={
                        lastColumnKey === "thrive_added"
                          ? fillStyle(widthFor("thrive_added") ?? MIN_WIDTH)
                          : widthStyle("thrive_added")
                      }
                      width={widthFor("thrive_added") ?? MIN_WIDTH}
                      isFill={lastColumnKey === "thrive_added"}
                      handlers={resizeHandlers}
                    />
                  </>
                )}
                {/* Dynamic annex column headers — descriptor-driven, and
                    click-to-sort by that criterion's score when `onSort` is
                    wired (AI-reviewed imports). Each is drag-resizable via the
                    right-edge handle; the inline `width` is the sanctioned
                    dynamic-value style (min==max==width pins the column under
                    `table-layout: auto`; the body rationale box is `w-full`). */}
                {columns.map((col) => {
                  const active = sort?.key === col.key;
                  // Committed width only — the live drag writes the <th> width
                  // imperatively (see moveResize), so no per-move re-render.
                  const width = widthFor(col.key) ?? pinnedFallback(col.key, DEFAULT_ANNEX_WIDTH);
                  return (
                    <th
                      key={col.key}
                      data-testid="annex-header"
                      data-key={col.key}
                      // Same string as `data-key`, deliberately not reusing it.
                      // `data-key` is IDENTITY (the handle carries it too, and
                      // tests address columns by it); `data-col-key` is the
                      // measurement OPT-IN, and it is what
                      // `measureColumnWidths` selects on. Keeping them separate
                      // is what lets a future column be identifiable without
                      // being content-fitted — `contact_info` is exactly that
                      // case. They must agree in value; nothing asserts it, and
                      // a divergence shows up as a column silently falling back
                      // to its cap.
                      data-col-key={col.key}
                      data-sort={active ? sort?.dir : undefined}
                      aria-sort={
                        active
                          ? sort?.dir === "asc"
                            ? "ascending"
                            : "descending"
                          : onSort
                            ? "none"
                            : undefined
                      }
                      className="relative px-3 py-2"
                      // Through `widthStyle`, not hand-rolled. This was the one
                      // width-bearing `<th>` that bypassed the function whose
                      // docstring carries the lockstep warning — i.e. a THIRD
                      // independent copy of the pin, which is the exact shape
                      // that warning describes (`/pr-review` iter 9, Agents A
                      // N2 + B). Equivalent by construction now rather than by
                      // inspection: the fallback is never null here, so the
                      // style is always emitted and `reactPinsWidth` is true.
                      // `width`, not a second `pinnedFallback(...)` call. Two
                      // evaluations of a COMPUTED fallback that must agree is
                      // the real lockstep hazard — worse than the constants
                      // this pattern was borrowed from, because a computed
                      // value can differ between two calls in a way a shared
                      // import cannot. `widthStyle` re-applies the same
                      // `widthFor(key) ??`, so this is identical by
                      // construction rather than by inspection.
                      style={
                        lastColumnKey === col.key ? fillStyle(width) : widthStyle(col.key, width)
                      }
                    >
                      {/* `sortable`, the SAME boolean the measurement signature
                          keys on — see its declaration for why `!!` rather
                          than `Boolean()` is what makes that possible.
                          I declined this once, on the argument that the
                          truthiness check here IS the type narrowing and the
                          only escapes were an assertion or an `onSort?.()`
                          whose no-op branch can never fire. True of
                          `Boolean()`, and I stated it as if it were true of
                          every alias — a two-option menu when there were
                          three. The reviewer who proposed it tested the
                          compiler instead of arguing back
                          (`/pr-review` Agent C, N2). */}
                      {sortable ? (
                        <button
                          type="button"
                          data-testid="annex-sort"
                          className="inline-flex items-center gap-1 hover:text-text"
                          onClick={() => onSort(col.key)}
                        >
                          {col.label}
                          {/* Always show a sort affordance so the feature is
                              discoverable — a dim ↕ when inactive, the bright
                              active direction (▲/▼) when this column is sorted. */}
                          <span
                            aria-hidden="true"
                            className={active ? "text-accent" : "text-text-muted"}
                          >
                            {active ? (sort?.dir === "desc" ? "▼" : "▲") : "↕"}
                          </span>
                        </button>
                      ) : (
                        col.label
                      )}
                      {/* Drag-to-resize handle — sibling of the sort button (not
                          nested: no button-in-button). Same component every
                          other column now uses; the annex columns differ only in
                          being pinned to a default width from birth. */}
                      <ColumnResizeHandle
                        columnKey={col.key}
                        label={col.label}
                        isFill={lastColumnKey === col.key}
                        width={width}
                        handlers={resizeHandlers}
                      />
                    </th>
                  );
                })}
                {isBct && (
                  <>
                    <th className="relative px-3 py-2" style={widthStyle("bct_position")}>
                      Company Map Matched Position
                      <ColumnResizeHandle
                        columnKey="bct_position"
                        label="Company Map Matched Position"
                        width={widthFor("bct_position")}
                        handlers={resizeHandlers}
                      />
                    </th>
                    <th className="relative px-3 py-2" style={widthStyle("bct_company")}>
                      Company Map Matched Company
                      <ColumnResizeHandle
                        columnKey="bct_company"
                        label="Company Map Matched Company"
                        width={widthFor("bct_company")}
                        handlers={resizeHandlers}
                      />
                    </th>
                    <th
                      className="relative px-3 py-2"
                      style={
                        lastColumnKey === "bct_years"
                          ? fillStyle(widthFor("bct_years") ?? MIN_WIDTH)
                          : widthStyle("bct_years")
                      }
                    >
                      Company Map Years
                      {/* `?? MIN_WIDTH`, and it is not cosmetic. This handle
                          feeds `beginResize`'s `startWidth` and `keyResize`'s
                          `current`, and for a fill column a gesture moves the
                          FLOOR. Left undefined, both fell through to the
                          MEASURED width — so on a Company Map list filling at
                          876px, one `ArrowLeft` (which means NARROWER) committed
                          860 as the floor: a 732px widening, persisted globally
                          for every Company Map import, and invisible to
                          `movesWrongWay` because the rule was comparing against
                          the fill rather than the floor (`/pr-review` round 10,
                          Agent A MAJOR 8).
                          The other two fill call sites were already anchored to
                          their floor — `contact_info` via `?? CONTACT_INFO_WIDTH`
                          and the annex columns via `pinnedFallback` — which is
                          why only this one bit, and why the divergence was hard
                          to see: the branch that gets tested is the branch where
                          the two numbers happen to agree. */}
                      <ColumnResizeHandle
                        columnKey="bct_years"
                        label="Company Map Years"
                        isFill={lastColumnKey === "bct_years"}
                        width={widthFor("bct_years") ?? MIN_WIDTH}
                        handlers={resizeHandlers}
                      />
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {contacts.map((row) => (
                <tr
                  key={row.import_contact_id}
                  data-testid="contact-row"
                  data-person-open={isPersonOpen(row) ? "true" : undefined}
                  data-unselected={isDimmed(row) ? "true" : undefined}
                  // The whole row hovers and marks the open person; the frozen
                  // cells follow through `frozenCellStateClass`.
                  className={`border-b border-border text-body text-text ${rowStateClass(row)}`}
                >
                  {showSelection && (
                    <td
                      // Opaque and themed, NOT `bg-bg` or `bg-table-surface`:
                      // see `FROZEN_CELL_SURFACE_CLASS`. The Name cell below
                      // takes the same constant. KEEP `sticky` AND `z-10`
                      // together: they make this cell a stacking context, and
                      // the tint overlay's `before:-z-10` sits under the
                      // content only inside it — drop either and the overlay
                      // falls beneath the table (`FROZEN_CELL_TINT_CLASS`).
                      className={`sticky left-0 z-10 w-12 min-w-[3rem] ${FROZEN_CELL_SURFACE_CLASS} ${frozenCellStateClass(row, true)} px-3 py-2`}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select ${fullName(row)}`}
                        data-testid="row-select"
                        // Only selectable rows can be checked (shared isExportable):
                        // already-in-Thrive + W1 duplicates are disabled; failed/W2 stay
                        // selectable for retry / Update-anyway.
                        disabled={!isExportable(row)}
                        // In all-filtered mode every SELECTABLE row reads
                        // checked — the selection is the filter scope, not an
                        // id list. Disabled rows stay unchecked: they cannot
                        // be unchecked, so a checked-and-frozen box would
                        // claim a membership the recruiter cannot revoke.
                        checked={
                          inAllFilteredMode
                            ? isExportable(row)
                            : selectedIds.has(row.import_contact_id)
                        }
                        // `selectableIds` rides along so unchecking one row in
                        // all-filtered mode can drop to an explicit selection
                        // of what is on screen (see `useContactsSelection`).
                        onChange={() => onToggleSelect(row.import_contact_id, selectableIds)}
                      />
                    </td>
                  )}
                  <td
                    // The stripe marks the row's FIRST frozen cell, which is
                    // this one only when there is no checkbox column (the AI
                    // Review page, see `frozenCellStateClass`).
                    // `sticky` + `z-10` stay together: the tint overlay's
                    // `before:-z-10` depends on the stacking context they make.
                    className={`sticky ${nameStickyLeft} z-10 ${FROZEN_CELL_SURFACE_CLASS} ${frozenCellStateClass(row, !showSelection)} px-3 py-2 ${frozenEdgeClass}`}
                  >
                    <div className="flex items-center gap-2">
                      <PersonRowEntry
                        row={row}
                        handle={personHandleOf(row)}
                        open={isPersonOpen(row)}
                        onOpen={onOpenPerson}
                        avatar={
                          <Avatar
                            // src SELECTION: the ONLY avatar source is the durable S3
                            // copy. A row WITH a stored key renders its presigned S3
                            // URL (undefined while the presign is in flight → initials
                            // monogram). A row with NO stored key gets initials too. We
                            // deliberately never render the expiring licdn
                            // `profile_image_url` — it 403s on first paint and flashes a
                            // broken image.
                            // A row with no stored key on a Thrive-sourced list
                            // resolves by the Thrive contact it was pulled from
                            // (04; per row since 05, `rowAvatarUrl`).
                            src={rowAvatarUrl(row, resolvedAvatars, thriveAvatars)}
                            name={fullName(row) === "—" ? null : fullName(row)}
                            size="lg"
                          />
                        }
                      >
                        {/* `[overflow-wrap:anywhere]`, for the same reason the
                            annex rationale box carries it: a pinned `<th>` width
                            holds under `table-layout: auto` only while every body
                            cell's min-content stays at or below it, so a cell
                            holding one long unbreakable token defeats the resize
                            handle above it — the gesture runs and the column does
                            not move.

                            KEPT DEFENSIVELY, and the distinction is worth stating
                            because the reason it was added has expired. It was
                            added when item 8 had moved an email into this cell;
                            item 8 was then reversed and the address lives in its
                            own column again, so what this cell holds today is a
                            name and `<company>, <role>` — breakable text, with no
                            token long enough to matter. Nothing goes red if you
                            delete it. Leave it: a company name with no spaces is
                            a real input, and this cell is `sticky` so a blow-out
                            here is more visible than most.

                            It is a WRAP rule, not truncation — item 6 stands.

                            A `<span>`, not a `<div>`: this sits inside the row
                            `<button>`, which allows phrasing content only. */}
                        <span className="flex min-w-0 flex-col [overflow-wrap:anywhere]">
                          {/* Plain text, no link: the row is the side panel's
                              entry and LinkedIn lives on the icon beside it
                              (`LinkedInProfileLink`). */}
                          <span
                            data-testid="person-name"
                            className={isDimmed(row) ? "line-through opacity-50" : undefined}
                          >
                            {highlightMatch(fullName(row), nameQuery)}
                          </span>
                          {/* Last known position, as resolved at import time. No
                              element at all when the contact has neither half —
                              an empty line would add row height for nothing. */}
                          <CurrentPositionLine
                            row={row}
                            positionQuery={currentPositionFilter}
                            companyQuery={currentCompanyFilter}
                          />
                        </span>
                      </PersonRowEntry>
                      <LinkedInProfileLink row={row} />
                    </div>
                  </td>
                  {isPreview && richPreview && (
                    <td
                      className="px-3 py-2 align-top text-text-secondary"
                      data-testid={`location-cell-${row.import_contact_id}`}
                    >
                      {row.location ?? "—"}
                    </td>
                  )}
                  {!isPreview && (
                    <td
                      data-testid={`contact-status-cell-${row.import_contact_id}`}
                      className="px-3 py-2"
                    >
                      {/* What happened to this contact IN THIS SEARCH — see
                        `SearchStatusCellContent`. The server's
                        `review_status` still decides it; what this cell
                        stopped doing is answering the Thrive ID column's
                        question in this column's words. */}
                      <SearchStatusCellContent
                        row={row}
                        onUndoLeaveOut={onUndoLeaveOut}
                        onUndoDifferentPerson={onUndoDifferentPerson}
                        identifiedOnAdded={showThriveColumns && thriveColumns?.derived !== true}
                      />
                    </td>
                  )}
                  {(!isPreview || richPreview) && (
                    <td className="px-3 py-2">
                      {/* `isInThrive` is the SHARED predicate (see
                        ReviewStatusFilterBar). The `&& export_status !==
                        "skipped"` clause that used to sit here is gone.
                      
                        SUPERSEDED, 2026-08-20 (cl.4 items 11 + 13): the
                        2026-07-15 review hid the id on a skipped row because
                        "a live link would misleadingly read as already
                        linked/exported". That reasoning does not survive item
                        11 — the "Not in Thrive" filter counts this exact row as
                        IN Thrive, so hiding the id made the cell and the filter
                        disagree about the same contact. The concern is answered
                        by adjacency instead: the Export column immediately left
                        says `skipped`, so the id reads as "Thrive has them",
                        not "we just wrote them". `ExportToThrivePanel` carries
                        the same reversal, for the same reason.
                      
                        Still true, and still load-bearing: Compare attaches
                        that `contact_id_thrive` for export's OWN
                        update-vs-create decision on a future re-export
                        (`resolve_export_plan` reads it directly) — do NOT stop
                        persisting it. */}
                      {isInThrive(row) ? (
                        // THRIVE'S OWN photo sits immediately left of the id, so
                        // the eye can compare it against the row's own avatar in
                        // the name cell without opening anything. The pair is the
                        // point: left = what we imported, right = who Thrive
                        // thinks that is. The cell itself is the shared
                        // `ThriveIdCell` (2026-10-07, epic 2026-10-06 sub-task 05
                        // — the Candidates tab renders the same link WITHOUT the
                        // photo); the reasons the slot renders on every linked
                        // row, why `name` is deliberately not passed, and where
                        // the test id sits are documented on the component.
                        //
                        // `thrive_avatar_url` null (Thrive has none on file) and a
                        // URL that fails to load (the photo was REPLACED in
                        // Thrive — the object name carries an `epoch_ms`, so the
                        // old one may be gone; these URLs are public and unsigned,
                        // so nothing expires on a timer and this is rare) both
                        // render the silhouette. No error UI and no retry for
                        // that case, by design.
                        <ThriveIdCell
                          contactId={row.contact_id_thrive}
                          showAvatar
                          avatarSrc={row.thrive_avatar_url}
                        />
                      ) : hasCompareVerdict(row) ? (
                        // A compare classified THIS ROW and Thrive has no such
                        // contact. Only now is "Not found" a true statement
                        // (cl.4 item 13) — and the per-row verdict is what makes
                        // it true: the import-level `compareHasRun` that used to
                        // gate this said "Not found" for every unmatched row of
                        // a PARTIALLY compared import, including rows no compare
                        // had reached.
                        //
                        // DATED, where the id above is not. The asymmetry is the
                        // point: an id is durable, a negative result ages —
                        // "not in Thrive" from three weeks ago is worth
                        // re-checking.
                        <span
                          data-testid="thrive-not-found"
                          // `text-caption` is not decoration: without a size
                          // class this inherits the table body size, so the
                          // Thrive ID column's word rendered LARGER than the
                          // status column's — the same sentence at two sizes,
                          // one cell apart. Both columns answer a question
                          // about the same row; neither outranks the other.
                          className="flex flex-col gap-0.5 text-caption text-text-muted"
                        >
                          {/* It had NO icon, alone among the states in these two
                            columns — every `review_status` in the cell to the
                            left carries one, and this cell's other two states
                            now do too. `SearchX`: a lookup was performed and
                            came back empty, which is exactly the sentence.
                            Never alone — the word is the accessible name. */}
                          <span className="inline-flex items-center gap-1">
                            <SearchX aria-hidden="true" className={STATUS_ICON_CLASS} />
                            {THRIVE_NOT_FOUND}
                          </span>
                          {/* `compare-date-line` keeps its NAME and its
                            MEANING — the compare verdict's date — and changes
                            only its column, and now its LINE: it sits under
                            the word rather than trailing it, with the clock
                            the narrow-column argument used to cost it. */}
                          <span data-testid="compare-date-line">
                            {row.compared_at
                              ? formatStatusDate(new Date(row.compared_at), new Date())
                              : // A legacy pre-0040 verdict: classified, but
                                // from before `compared_at` existed. Say the
                                // record is missing rather than nothing at all.
                                DATE_NOT_RECORDED}
                          </span>
                        </span>
                      ) : (
                        // No compare has ever classified this row, so we do not
                        // know. Saying "Not found" here would assert a lookup
                        // nobody performed. No date, and it needs none: this
                        // means never.
                        //
                        // The bare DASH this replaced was ambiguous between "no"
                        // and "unknown" — the same ambiguity "Not added yet"
                        // removes one column to the left. Amber is kept: it is
                        // the header warning icon's colour, which is what makes
                        // the two read as one statement, and this column is now
                        // the only place the "nobody has looked" signal lives.
                        <span
                          data-testid="thrive-not-compared"
                          className="inline-flex items-center gap-1 text-caption text-warning-ink"
                        >
                          {/* The LAST state in these two columns without a
                            glyph, and it gets the warning triangle rather
                            than a neutral one.
                            
                            This does NOT contradict the rule one column left,
                            where the three "Not added yet" states share a
                            single neutral glyph. There the icon would be
                            saying something the WORD declines to say — three
                            different Thrive-side facts under one search-side
                            sentence. Here the word is already a distinct
                            answer to this column's own question ("do we know
                            this person in Thrive?" — we have not looked), so
                            the glyph reinforces it instead of out-running it.
                            It takes the ink the element already carries, so
                            colour and shape say the one thing. */}
                          <AlertTriangle aria-hidden="true" className={STATUS_ICON_CLASS} />
                          {/* "Not checked" on the `richPreview` surface (round
                            2, sub-task R2-B) — Combine & Filter's own
                            per-source Compare state, distinct from this
                            import-level "has Compare ever run for THIS
                            list" reading `THRIVE_NOT_COMPARED` states for
                            the detail page. Only reachable here when
                            `richPreview` is set, so the detail page's copy
                            is unchanged. */}
                          {isPreview ? "Not checked" : THRIVE_NOT_COMPARED}
                        </span>
                      )}
                    </td>
                  )}
                  {isPreview && richPreview && (
                    <td
                      className="px-3 py-2 align-top text-text-secondary"
                      data-testid={`lists-cell-${row.import_contact_id}`}
                    >
                      {(() => {
                        const lists = sourceListsFor?.(row);
                        if (!lists || lists.length === 0) return "—";
                        return (
                          <span className="flex flex-wrap gap-1">
                            {lists.map((list) => (
                              <span
                                key={list.id}
                                className="rounded-pill border border-border bg-surface px-1.5 py-0.5 text-caption"
                              >
                                {list.title}
                              </span>
                            ))}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  {isPreview && richPreview && previewStageColumn && (
                    <td
                      className="px-3 py-2 align-top"
                      data-testid={`preview-stage-cell-${row.import_contact_id}`}
                    >
                      <StagePill stage={row.candidacy_stage} />
                    </td>
                  )}
                  {/* Contact info — both addresses, their provenance badges and
                    any enrichment warning. Restored as a column after the
                    third-line experiment: item 7 requires it as a base
                    column, and the bug that motivated moving it (the address
                    shredding) is a squeeze problem, fixed by pinning the
                    column's width. */}
                  {!isPreview && (
                    <td data-testid="enrich-data-cell" className="px-3 py-2 align-top">
                      <EnrichData row={row} clip />
                    </td>
                  )}
                  {showThriveColumns && thriveColumns && (
                    <>
                      <td data-testid="thrive-stage-cell" className="px-3 py-2 align-top">
                        <span className="flex flex-col items-start gap-1">
                          <StagePill stage={row.candidacy_stage} />
                          {row.candidacy_highest_stage &&
                          row.candidacy_highest_stage !== row.candidacy_stage ? (
                            <span
                              data-testid="thrive-highest-stage"
                              className="whitespace-nowrap text-caption text-text-secondary"
                            >
                              <span aria-hidden="true">↑ </span>
                              <span className="sr-only">Highest stage: </span>
                              {row.candidacy_highest_stage}
                            </span>
                          ) : null}
                          {row.candidacy_stage === REJECTED_STAGE &&
                          row.candidacy_rejection_reason ? (
                            <span
                              data-testid="thrive-rejection-reason"
                              className="max-w-[16rem] text-caption text-text-secondary"
                            >
                              {row.candidacy_rejection_reason}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td
                        data-testid="thrive-in-stage-cell"
                        className="whitespace-nowrap px-3 py-2 align-top tabular-nums"
                      >
                        {formatDaysInStage(
                          row.candidacy_stage_since,
                          new Date(thriveColumns.importedAt)
                        )}
                      </td>
                      <td
                        data-testid="thrive-added-cell"
                        className="whitespace-nowrap px-3 py-2 align-top"
                      >
                        {formatDate(row.candidacy_added_at)}
                      </td>
                    </>
                  )}
                  {/* Dynamic annex cells — the score badge plus, when present, the
                    AI Review rationale shown in-cell. The prompt asks the
                    model for one short evidence-led fragment (~120 chars),
                    so the box usually wraps to a few lines and the row grows
                    to fit — no fixed height, no scrollbar (client feedback).

                    There is NO hard cap behind that "~120". #746 wrote
                    "hard cap 180" here, which was true when it was written
                    and is not any more: #711 (bugs-found-live 2) removed the
                    cap and all three of its enforcement copies, because the
                    worker was cutting rationales to 177 chars plus an
                    ellipsis BEFORE persisting them. What lands in this cell
                    is now the model's own text at whatever length it chose.

                    The `line-clamp-[8]` that used to bound this is GONE
                    (cl.4 item 6 — never truncate), and the two changes agree:
                    nothing shortens the value and nothing clips its display.
                    Consequence worth stating rather than discovering — a long
                    rationale renders at its full height, and that is now
                    reachable by current rows, not only by legacy ones scored
                    under the old 600-char cap. The cell's `title` is a hover
                    convenience, not a reveal affordance. */}
                  {columns.map((col) => {
                    const rationale = rationaleFor?.(row.import_contact_id, col.key);
                    return (
                      <td key={col.key} data-testid="annex-cell" className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <ScoreBadge value={row.annex?.[col.key]} kind={col.kind} />
                          {rationale ? (
                            <div
                              data-testid="annex-rationale"
                              // `title` is a hover convenience, NOT a reveal
                              // affordance: nothing is clipped any more (cl.4
                              // item 6 deleted the `line-clamp-[8]`, and
                              // `ContactsTable.test.tsx` asserts its absence).
                              // The previous wording said the opposite and
                              // would have invited someone to restore the clamp
                              // on its authority.
                              title={rationale}
                              // LOAD-BEARING: keep `[overflow-wrap:anywhere]`. The
                              // annex column width is pinned on the `<th>` only;
                              // under `table-layout: auto` that pin holds solely
                              // because every body cell's min-content stays ≤ the
                              // set width. `anywhere` (unlike `break-word`) breaks
                              // an unbreakable token, keeping min-content small.
                              // Swapping it back to `break-words` silently lets a
                              // long unbroken string out-vote the header width.
                              className="w-full whitespace-pre-wrap [overflow-wrap:anywhere] rounded-sm border border-border bg-bg px-2 py-1 text-body leading-snug text-text-secondary"
                            >
                              {highlightNotes(rationale, notesTerms)}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    );
                  })}
                  {isBct && (
                    <>
                      {/* Same rule as the name cell: these two are resizable
                        now, and a company name with a long unbroken token
                        would otherwise out-vote the pinned width. */}
                      <td className="px-3 py-2 text-body text-text [overflow-wrap:anywhere]">
                        {row.bct_match_reason?.matched_job_title ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-body text-text [overflow-wrap:anywhere]">
                        {row.bct_match_reason?.matched_company_name ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-body text-text">
                        {formatYears(
                          row.bct_match_reason?.matched_start_year,
                          row.bct_match_reason?.matched_end_year
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
